// ── Migration Review Queue ──
// Operator-facing surface for reviewing & approving imported jobs
// (Jobber CSV + JSON). Pre-approval, these jobs are hidden from every
// operational surface via isMigrationApproved().
//
// Anchor: mem://features/jobber-import-bridge (beta)

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { useAppData } from "@/data/AppDataContext";
import { PROJECT_TYPES, type Job, type Issue, type JobPhotoGroup, type MigrationBypassStage, type ProjectType, type UnmappedSkuReviewItem } from "@/data/types";
import { PushNextStepPanel, type PushNextStep } from "@/components/quick-actions/PushNextStepPanel";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronLeft, ChevronRight, Eye, Download, Upload, FileText } from "lucide-react";
import { toast } from "sonner";
import {
  mapJobberCsvRow,
  mapJobberJsonProject,
  parseCsv,
} from "@/data/import/jobberMapper";
import type { MigrationAiScopeDraft, MigrationAiScopeMaterialDraft, MigrationContractPreviewDraft, MigrationJsonProject } from "@/data/import/migrationTypes";
import {
  contentHash,
  createBatch,
  hasImportedKey,
  jobKey,
  markImportedKey,
} from "@/data/import/importBatch";
import { computeReviewFlags, hasBlockingFlag, BLOCKING_FLAGS } from "@/data/import/reviewFlags";
import { filterPendingReview } from "@/data/import/migrationVisibilityGuard";
import { JobService } from "@/services/job.service";
import { CustomerService } from "@/services/customer.service";
import { getProducts, getProductsForSkuMatching } from "@/services/productRegistry.service";
import { WarehousePageService } from "@/services/warehouse-page.service";
import type { ProductCategory, RegistryProduct } from "@/data/ProductRegistryContext";
import { useRole } from "@/data/RoleContext";
import { QuickRegisterProductModal } from "@/components/product-registry/QuickRegisterProductModal";
import { replaceJobMaterialsSnapshot } from "@/services/inventory.service";
import { supabase, supabaseUrl, supabaseAnonKey } from "@/lib/supabase";
import { createSignedStorageFullImageUrl } from "@/lib/storageImageUrls";
import { uploadWithXhr } from "@/lib/storageXhrUpload";
import { formatPhoneNumber } from "@/lib/phoneFormat";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import {
  IMPORT_PHOTO_BUCKET,
  extractImportPhotoStoragePath,
  resolveImportPhotoThumbnailUrl,
  resolveImportPhotoUrl,
} from "@/data/import/importPhotoUrls";
import { computeFileHash, isSessionDuplicate, registerUploadHash } from "@/lib/photoDedup";

type MaterialLine = NonNullable<Job["materialsList"]>[number];
type ImportScopeGenerationResponse = {
  ok?: boolean;
  model?: string;
  usage?: unknown;
  generation?: MigrationAiScopeDraft;
};
type ImportContractPreviewResponse = {
  ok?: boolean;
  model?: string;
  usage?: unknown;
  preview?: MigrationContractPreviewDraft;
  generation?: MigrationAiScopeDraft;
};
type ScopeDraftDialogState = {
  rowId: string;
  generation: MigrationAiScopeDraft;
  selectedMaterialHintIndexes: number[];
  model?: string;
  usage?: unknown;
};
const IMPORT_CONTRACT_BUCKET = "job-documents";
// "permitting" is intentionally excluded — permitting is now required on all jobs.
const DEFAULT_MIGRATION_BYPASSES: MigrationBypassStage[] = ["build_review", "ordering"];
const MIGRATION_BYPASS_OPTIONS: { key: MigrationBypassStage; label: string }[] = [
  { key: "build_review", label: "Build Review" },
  { key: "permitting", label: "Permitting" },
  { key: "ordering", label: "Ordering" },
];

// SKU validation for materials during migration approval
async function validateAndFlagUnmappedMaterials(materials: MaterialLine[]): Promise<{
  unmappedSkus: UnmappedSkuReviewItem[];
}> {
  if (!materials || materials.length === 0) {
    return { unmappedSkus: [] };
  }

  // Get existing products from registry
  const existingProducts = await getProducts();
  const existingSkus = new Set(existingProducts.map((p) => p.sku.toUpperCase()));
  const existingNames = new Map(existingProducts.map((p) => [p.productName.toLowerCase(), p]));

  const unmappedSkus: UnmappedSkuReviewItem[] = [];

  for (const material of materials) {
    const normalizedSku = (material.sku || "").toUpperCase().trim();
    const normalizedName = (material.itemName || "").toLowerCase().trim();

    // Check if SKU exists in registry
    const skuExists = normalizedSku && existingSkus.has(normalizedSku);

    // Check if name matches existing product
    const nameMatches = normalizedName && existingNames.has(normalizedName);

    // If neither exists, flag as unmapped
    if (!skuExists && !nameMatches && normalizedSku) {
      // Generate proposed SKU using the same logic as productRegistry.service
      const category = inferCategoryFromMaterialName(material.itemName || normalizedSku);
      const proposedSku = generateDeterministicSku(material.itemName || normalizedSku, category);
      unmappedSkus.push({
        sku: material.sku,
        itemName: material.itemName,
        proposedSku,
      });
    }
  }

  return { unmappedSkus };
}

function normalizeSkuTokenForMigration(value: string): string {
  return value
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32);
}

function normalizeMaterialSku(value: unknown): string {
  return String(value ?? "").trim().toUpperCase();
}

function inferCategoryFromMaterialName(name: string): ProductCategory {
  const lookup = name.toLowerCase();
  if (lookup.includes("door") || lookup.includes("glass") || lookup.includes("enclosure")) return "Glass/Enclosure";
  if (lookup.includes("wall")) return "Wall System";
  if (lookup.includes("base") || lookup.includes("pan")) return "Base";
  if (lookup.includes("seat") || lookup.includes("bench") || lookup.includes("corner") || lookup.includes("niche") || lookup.includes("shelf") || lookup.includes("caddy") || lookup.includes("add-on") || lookup.includes("addon")) {
    return "Add-On";
  }
  return "Other";
}

function categoryPrefixForMigration(category: ProductCategory): string {
  switch (category) {
    case "Wall System": return "WALL";
    case "Base": return "BASE";
    case "Glass/Enclosure": return "GLASS";
    case "Hardware": return "HARD";
    case "Add-On": return "ADD";
    default: return "OTH";
  }
}

function generateDeterministicSku(name: string, category: ProductCategory): string {
  const normalized = normalizeSkuTokenForMigration(name);
  const prefix = categoryPrefixForMigration(category);
  return prefix + "-AUTO-" + (normalized || "ITEM");
}

function normalizeImportedMaterialLines(materials: MaterialLine[]): MaterialLine[] {
  return materials.map((item) => {
    const rawName = String(item.itemName ?? "").trim();
    const quantityMatch = rawName.match(/^\s*\((\d+)\)\s*/);
    const extractedQty = quantityMatch ? Number(quantityMatch[1]) : 1;
    const cleanedName = quantityMatch ? rawName.slice(quantityMatch[0].length) : rawName;
    const sku = String(item.sku ?? "").trim();
    const finalSku = sku || `IMP-${normalizeSkuTokenForMigration(cleanedName || "Imported Item").slice(0, 28) || "ITEM"}`;
    return {
      ...item,
      itemName: cleanedName,
      quantityRequired: quantityMatch ? extractedQty : (item.quantityRequired ?? 1),
      sku: finalSku,
    };
  });
}

function normalizeAiMaterialHint(material: MigrationAiScopeMaterialDraft): MigrationAiScopeMaterialDraft {
  const confidence = Number(material.confidence ?? 0);
  return {
    itemName: String(material.itemName ?? "").trim(),
    sku: String(material.sku ?? "").trim(),
    quantity: Math.max(1, Math.round(Number(material.quantity ?? 1) || 1)),
    confidence: Number.isFinite(confidence) ? Math.min(1, Math.max(0, confidence)) : 0,
    notes: String(material.notes ?? "").trim(),
  };
}

function materialLineMergeKey(material: Pick<MaterialLine, "sku" | "itemName">): string {
  const sku = normalizeMaterialSku(material.sku);
  if (sku) return `sku:${sku}`;
  return `name:${String(material.itemName ?? "").trim().toLowerCase()}`;
}

function aiMaterialHintToMaterialLine(
  material: MigrationAiScopeMaterialDraft,
  catalogProduct?: RegistryProduct,
): MaterialLine | null {
  const normalized = normalizeAiMaterialHint(material);
  if (!normalized.itemName && !normalized.sku) return null;
  const itemName = catalogProduct?.productName || normalized.itemName || normalized.sku || "Imported material";
  const sku = normalizeMaterialSku(catalogProduct?.sku || normalized.sku) || `IMP-${normalizeSkuTokenForMigration(itemName).slice(0, 28) || "ITEM"}`;
  return {
    sku,
    itemName,
    quantityRequired: normalized.quantity,
    quantityAllocated: 0,
    quantityStaged: 0,
    quantityInstalled: 0,
    missingItems: 0,
    fieldPurchase: false,
    inventoryStatus: "In Stock",
    inventoryAssignment: "Assigned",
    lifecycleStage: "Ordered",
    unitCost: catalogProduct?.cost,
    unitPrice: catalogProduct?.price,
  };
}

function mergeMaterialHintsIntoMaterials(
  existingMaterials: MaterialLine[],
  selectedHints: MigrationAiScopeMaterialDraft[],
  catalogProductBySku?: Map<string, RegistryProduct>,
): MaterialLine[] {
  const nextMaterials = normalizeImportedMaterialLines(existingMaterials);
  const indexByKey = new Map<string, number>();
  nextMaterials.forEach((material, index) => {
    indexByKey.set(materialLineMergeKey(material), index);
  });

  for (const hint of selectedHints) {
    const catalogProduct = catalogProductBySku?.get(normalizeMaterialSku(hint.sku));
    const material = aiMaterialHintToMaterialLine(hint, catalogProduct);
    if (!material) continue;

    const key = materialLineMergeKey(material);
    const existingIndex = indexByKey.get(key);
    if (existingIndex === undefined) {
      indexByKey.set(key, nextMaterials.length);
      nextMaterials.push(material);
      continue;
    }

    const existing = nextMaterials[existingIndex];
    nextMaterials[existingIndex] = {
      ...existing,
      sku: material.sku || existing.sku,
      itemName: material.itemName || existing.itemName,
      quantityRequired: material.quantityRequired,
    };
  }

  return normalizeImportedMaterialLines(nextMaterials);
}

function defaultSelectedMaterialHintIndexes(generation: MigrationAiScopeDraft): number[] {
  return generation.extractedMaterials.map((_, index) => index);
}

function randomStableId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

const UNGROUPED_UPLOAD_GROUP = "__ungrouped__";

function uploadKey(rowId: string, file: File, groupId?: string): string {
  return `upload:${rowId}:${groupId ?? UNGROUPED_UPLOAD_GROUP}:${file.name}:${file.size}`;
}

function uploadScopeKey(rowId: string, groupId?: string): string {
  return `upload:${rowId}:${groupId ?? UNGROUPED_UPLOAD_GROUP}`;
}

type UploadEntry =
  | { status: "hashing" | "saving" | "done" | "error" }
  | { status: "uploading"; fraction: number }
  | { status: "error"; error: string };

type UploadBatch = {
  total: number;
};

function progressForUploadEntry(entry: UploadEntry): number {
  if (entry.status === "done") return 1;
  if (entry.status === "saving") return 0.98;
  if (entry.status === "uploading") return Math.max(0, Math.min(0.95, entry.fraction));
  if (entry.status === "hashing") return 0.02;
  return 0;
}

function PhotoUploadStatusRow({
  rowId,
  groupId,
  statusByKey,
  batchByScope,
}: {
  rowId: string;
  groupId?: string;
  statusByKey: Record<string, UploadEntry>;
  batchByScope: Record<string, UploadBatch>;
}) {
  const prefix = `upload:${rowId}:${groupId ?? UNGROUPED_UPLOAD_GROUP}:`;
  const scopeKey = uploadScopeKey(rowId, groupId);
  const batch = batchByScope[scopeKey];
  const items = Object.entries(statusByKey)
    .filter(([k]) => k.startsWith(prefix))
    .map(([key, entry]) => ({ key, entry: entry as UploadEntry }));
  if (items.length === 0 && !batch) return null;

  const totalCount = Math.max(batch?.total ?? items.length, 1);
  const doneCount = items.filter(({ entry }) => entry.status === "done").length;
  const errorCount = items.filter(({ entry }) => entry.status === "error").length;
  const overallProgress =
    items.reduce((sum, { entry }) => sum + progressForUploadEntry(entry), 0) / totalCount;
  const overallPercent = Math.min(100, Math.round(overallProgress * 100));
  const overallLabel =
    errorCount > 0
      ? `${doneCount}/${totalCount} uploaded · ${errorCount} failed`
      : doneCount === totalCount
        ? `Uploaded ${doneCount}/${totalCount} photos`
        : `Uploading · ${doneCount}/${totalCount} uploaded`;

  return (
    <div className="space-y-2 rounded-md border bg-muted/30 p-2">
      <div className="space-y-1 rounded border bg-background/60 p-2">
        <div className="flex items-center justify-between gap-2 text-xs">
          <span className="font-medium">Overall upload progress</span>
          <span className={errorCount > 0 ? "text-destructive" : "text-muted-foreground"}>
            {overallLabel} · {overallPercent}%
          </span>
        </div>
        <Progress value={overallPercent} className="h-2" />
      </div>
      {items.map(({ key, entry }) => {
        const fileName = key.split(":").slice(3, -1).join(":") || key;
        const fraction = progressForUploadEntry(entry);
        let label = "";
        if (entry.status === "hashing") label = "Verifying…";
        else if (entry.status === "uploading") label = `Uploading ${Math.round(fraction * 100)}%`;
        else if (entry.status === "saving") label = "Saving…";
        else if (entry.status === "done") label = "Uploaded";
        else if (entry.status === "error") label = (entry as { error: string }).error;

        return (
          <div key={key} className="space-y-1">
            <div className="flex items-center gap-2 text-xs">
              <span className="flex-1 truncate min-w-0">{fileName}</span>
              <span
                className={`shrink-0 ${
                  entry.status === "error"
                    ? "text-destructive"
                    : entry.status === "uploading"
                      ? "text-primary"
                      : "text-muted-foreground"
                }`}
              >
                {label}
              </span>
            </div>
            {(entry.status === "uploading" || entry.status === "saving" || entry.status === "done") && (
              <Progress value={Math.round(fraction * 100)} className="h-1.5" />
            )}
          </div>
        );
      })}
    </div>
  );
}

function createPhotoGroup(label: string): JobPhotoGroup {
  return {
    id: randomStableId(),
    label: label.trim(),
    photoRefs: [],
    createdAt: new Date().toISOString(),
  };
}

function normalizePhotoGroups(partial: Partial<Job>): JobPhotoGroup[] {
  if (!Array.isArray(partial.photoGroups)) return [];
  return partial.photoGroups
    .map((group) => ({
      ...group,
      label: String(group.label ?? "").trim(),
      photoRefs: Array.isArray(group.photoRefs) ? group.photoRefs.filter(Boolean) : [],
      id: String(group.id || randomStableId()),
      createdAt: group.createdAt ?? new Date().toISOString(),
    }))
    .filter((group) => group.label || group.photoRefs.length > 0);
}

function cleanPhotoGroups(groups: JobPhotoGroup[]): JobPhotoGroup[] {
  return groups
    .map((group) => ({
      ...group,
      label: String(group.label ?? "").trim(),
      photoRefs: Array.isArray(group.photoRefs) ? group.photoRefs.filter(Boolean) : [],
    }))
    .filter((group) => group.label || group.photoRefs.length > 0);
}

function withPhotoGroups(current: Partial<Job>, groups: JobPhotoGroup[]): Partial<Job> {
  const nextGroups = cleanPhotoGroups(groups);
  return {
    ...current,
    photoGroups: nextGroups.length > 0 ? nextGroups : undefined,
  };
}

function hasPhotoGrouping(groups: JobPhotoGroup[]): boolean {
  return groups.some((group) => group.label || group.photoRefs.length > 0);
}

function appendUploadedPhotosToGroup(groups: JobPhotoGroup[], groupId: string, uploadedPaths: string[]): JobPhotoGroup[] {
  return groups.map((group) =>
    group.id === groupId
      ? { ...group, photoRefs: [...group.photoRefs, ...uploadedPaths] }
      : group,
  );
}

function removePhotoRefFromGroup(groups: JobPhotoGroup[], groupId: string, photoRef: string): JobPhotoGroup[] {
  return groups.map((group) =>
    group.id === groupId
      ? { ...group, photoRefs: group.photoRefs.filter((ref) => ref !== photoRef) }
      : group,
  );
}

function replacePhotoRefInGroup(groups: JobPhotoGroup[], groupId: string, oldPhotoRef: string, newPhotoRef: string): JobPhotoGroup[] {
  return groups.map((group) =>
    group.id === groupId
      ? { ...group, photoRefs: group.photoRefs.map((ref) => (ref === oldPhotoRef ? newPhotoRef : ref)) }
      : group,
  );
}

type StagedImport = {
  id: string;
  batchId: string;
  partial: Partial<Job>;
  importedAt: string;
};

type PhotoGalleryState = {
  title: string;
  photos: string[];
  index: number;
};

function splitName(fullName: string): { first: string; last: string } {
  const cleaned = String(fullName || "").trim();
  if (!cleaned) return { first: "Unknown", last: "Customer" };
  const parts = cleaned.split(/\s+/);
  return { first: parts[0], last: parts.slice(1).join(" ") || "" };
}

function normalizeImportedDate(value: string | undefined | null): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const m = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) {
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(d.getUTCDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }
  return "";
}

function emptyMaterialLine(): MaterialLine {
  return {
    sku: "",
    itemName: "",
    quantityRequired: 1,
    quantityAllocated: 0,
    quantityStaged: 0,
    quantityInstalled: 0,
    missingItems: 0,
    fieldPurchase: false,
    inventoryStatus: "In Stock",
    inventoryAssignment: "Assigned",
    lifecycleStage: "Ordered",
  };
}

function sanitizeStorageFileName(fileName: string): string {
  return String(fileName || "photo")
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 120) || "photo";
}

function encodeStoragePath(path: string): string {
  return path.split("/").map((segment) => encodeURIComponent(segment)).join("/");
}

function formatFileSize(bytes?: number): string {
  const size = Number(bytes ?? 0);
  if (!Number.isFinite(size) || size <= 0) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(size >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
}

function normalizeContractStoragePath(rawPath?: string | null): string {
  const value = String(rawPath ?? "").trim();
  if (!value) return "";
  if (value.includes("::")) {
    const [, path] = value.split("::", 2);
    return path ?? "";
  }
  const markers = [
    `/storage/v1/object/public/${IMPORT_CONTRACT_BUCKET}/`,
    `/storage/v1/object/sign/${IMPORT_CONTRACT_BUCKET}/`,
  ];
  for (const marker of markers) {
    const index = value.indexOf(marker);
    if (index === -1) continue;
    const raw = value.slice(index + marker.length).split("?")[0];
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
  return value.replace(/^\/+/, "");
}

function isPdfContractDocument(contract: Job["contractDocument"]): boolean {
  if (!contract?.filePath) return false;
  const mime = String(contract.mimeType ?? "").toLowerCase();
  const fileName = String(contract.fileName ?? contract.filePath ?? "").toLowerCase();
  return mime === "application/pdf" || fileName.endsWith(".pdf");
}

function isAllowedContractFile(file: File): boolean {
  const extensionAllowed = /\.(pdf|doc|docx)$/i.test(file.name);
  const mimeAllowed =
    file.type === "application/pdf" ||
    file.type === "application/msword" ||
    file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  return extensionAllowed || mimeAllowed;
}

function normalizeAiScopeDraft(
  generation: MigrationAiScopeDraft,
  model?: string,
  contractFileName?: string,
): MigrationAiScopeDraft {
  return {
    ...generation,
    scopeSummary: generation.scopeSummary ?? "",
    installerScopeItems: generation.installerScopeItems ?? [],
    materialsAndSelections: generation.materialsAndSelections ?? [],
    colorSelections: generation.colorSelections ?? [],
    panelBreakdown: generation.panelBreakdown ?? [],
    fieldNotes: generation.fieldNotes ?? [],
    exclusionsOrUnknowns: generation.exclusionsOrUnknowns ?? [],
    extractedMaterials: generation.extractedMaterials ?? [],
    warnings: generation.warnings ?? [],
    missingFields: generation.missingFields ?? [],
    model: model ?? generation.model,
    contractFileName: contractFileName ?? generation.contractFileName,
    generatedAt: generation.generatedAt ?? new Date().toISOString(),
  };
}

function isCachedScopeDraftForContract(
  draft: MigrationAiScopeDraft | undefined,
  contract: Job["contractDocument"],
): draft is MigrationAiScopeDraft {
  if (!draft?.scopeSummary) return false;
  if (!contract?.fileName || !draft.contractFileName) return true;
  return draft.contractFileName === contract.fileName;
}

function emptyContractPreviewDraft(): MigrationContractPreviewDraft {
  return {
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    addressLine1: "",
    city: "",
    state: "",
    postalCode: "",
    projectType: "Full Bathroom Remodel",
    quoteNumber: "",
    jobNumber: "",
    contractAmount: 0,
    signedDate: "",
    confidence: 0,
    warnings: [],
    missingFields: [],
  };
}

function normalizeContractPreviewDraft(
  preview: Partial<MigrationContractPreviewDraft> | undefined,
  model?: string,
  contractFileName?: string,
): MigrationContractPreviewDraft {
  const base = emptyContractPreviewDraft();
  return {
    ...base,
    ...(preview ?? {}),
    customerName: String(preview?.customerName ?? "").trim(),
    customerEmail: String(preview?.customerEmail ?? "").trim(),
    customerPhone: String(preview?.customerPhone ?? "").trim(),
    addressLine1: String(preview?.addressLine1 ?? "").trim(),
    city: String(preview?.city ?? "").trim(),
    state: String(preview?.state ?? "").trim(),
    postalCode: String(preview?.postalCode ?? "").trim(),
    projectType: String(preview?.projectType ?? base.projectType).trim(),
    quoteNumber: String(preview?.quoteNumber ?? "").trim(),
    jobNumber: String(preview?.jobNumber ?? "").trim(),
    contractAmount: Number.isFinite(Number(preview?.contractAmount)) ? Number(preview?.contractAmount) : 0,
    signedDate: String(preview?.signedDate ?? "").trim(),
    confidence: Math.min(1, Math.max(0, Number(preview?.confidence ?? 0))),
    warnings: Array.isArray(preview?.warnings) ? preview.warnings.filter(Boolean) : [],
    missingFields: Array.isArray(preview?.missingFields) ? preview.missingFields.filter(Boolean) : [],
    model: model ?? preview?.model,
    contractFileName: contractFileName ?? preview?.contractFileName,
    generatedAt: preview?.generatedAt ?? new Date().toISOString(),
  };
}

function matchProjectType(value: string): ProjectType {
  const normalized = value.trim().toLowerCase();
  const exact = PROJECT_TYPES.find((type) => type.toLowerCase() === normalized);
  if (exact) return exact;
  if (normalized.includes("tub") && normalized.includes("shower")) return "Tub to Shower Conversion";
  if (normalized.includes("full") && normalized.includes("bath")) return "Full Bathroom Remodel";
  if (normalized.includes("walk") && normalized.includes("tub")) return "Walk-in Tub Installation";
  if (normalized.includes("shower")) return "Shower Remodel";
  if (normalized.includes("window")) return "Window Install";
  if (normalized.includes("door")) return "Door Install";
  if (normalized.includes("floor")) return "Flooring";
  if (normalized.includes("roof")) return "Roofing";
  if (normalized.includes("paint")) return "Painting";
  if (normalized.includes("plumb")) return "Plumbing";
  if (normalized.includes("drywall")) return "Drywall";
  return "Other";
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? "");
      const marker = ";base64,";
      const markerIndex = result.indexOf(marker);
      resolve(markerIndex >= 0 ? result.slice(markerIndex + marker.length) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("Could not read file."));
    reader.readAsDataURL(file);
  });
}

function buildUploadedContractDocument(
  partial: Partial<Job>,
  file: File,
  storagePath: string,
): NonNullable<Job["contractDocument"]> {
  const existing = partial.contractDocument;
  const address = partial.address ?? { street: "", city: "", state: "", zip: "" };
  const now = new Date().toISOString();
  const customerAddress =
    existing?.customerAddress ??
    [address.street, address.city, address.state, address.zip].filter(Boolean).join(", ");

  return {
    id: existing?.id ?? `import-contract-${partial.sourceMeta?.externalProjectId ?? partial.id ?? randomStableId()}`,
    quoteId: existing?.quoteId ?? partial.sourceMeta?.externalProjectId ?? partial.id ?? "",
    customerName: existing?.customerName ?? partial.customerName ?? "Unknown Customer",
    customerEmail: existing?.customerEmail ?? partial.email ?? "",
    customerAddress,
    contractTotal: existing?.contractTotal ?? partial.financials?.contractValue ?? 0,
    homeownerSignature: existing?.homeownerSignature,
    homeownerSignedAt: existing?.homeownerSignedAt,
    repSignature: existing?.repSignature,
    repSignedAt: existing?.repSignedAt,
    termsAccepted: existing?.termsAccepted ?? Boolean(existing?.homeownerSignedAt),
    rescissionAcknowledged: existing?.rescissionAcknowledged ?? Boolean(existing?.homeownerSignedAt),
    status: existing?.status ?? (existing?.homeownerSignedAt ? "Signed" : "Pending"),
    createdAt: existing?.createdAt ?? now,
    approvedAt: existing?.approvedAt ?? existing?.homeownerSignedAt,
    sentToCustomerAt: existing?.sentToCustomerAt,
    dispositionNotes: existing?.dispositionNotes ?? "Imported contract uploaded during migration review.",
    ownershipType: existing?.ownershipType,
    poaDocumentUploaded: existing?.poaDocumentUploaded,
    hoaInvolved: existing?.hoaInvolved,
    cancellation: existing?.cancellation,
    selectedOptionsApproved: existing?.selectedOptionsApproved,
    configSnapshotHash: existing?.configSnapshotHash,
    configSnapshotAt: existing?.configSnapshotAt,
    paymentMethod: existing?.paymentMethod,
    depositAmount: existing?.depositAmount,
    filePath: `${IMPORT_CONTRACT_BUCKET}::${storagePath}`,
    fileName: file.name,
    mimeType: file.type || "application/octet-stream",
    fileSize: file.size,
    uploadedAt: now,
    attachmentId: existing?.attachmentId,
  };
}

const MAX_CONCURRENT_THUMBNAIL_LOADS = 4;
const GALLERY_THUMBNAIL_LIMIT = 6;
let activeThumbnailLoads = 0;
const queuedThumbnailLoads: Array<() => void> = [];

function runNextThumbnailLoad() {
  if (activeThumbnailLoads >= MAX_CONCURRENT_THUMBNAIL_LOADS) return;
  const next = queuedThumbnailLoads.shift();
  if (!next) return;
  activeThumbnailLoads += 1;
  next();
}

function resolveImportPhotoThumbnailUrlQueued(photoRef: string): Promise<string> {
  return new Promise((resolve, reject) => {
    queuedThumbnailLoads.push(() => {
      void resolveImportPhotoThumbnailUrl(photoRef)
        .then(resolve)
        .catch(reject)
        .finally(() => {
          activeThumbnailLoads = Math.max(0, activeThumbnailLoads - 1);
          runNextThumbnailLoad();
        });
    });
    runNextThumbnailLoad();
  });
}

function ImportPhotoPreview({
  photo,
  alt,
  className = "h-24 w-full rounded object-cover",
  placeholderClassName = "h-24 w-full",
}: {
  photo: string;
  alt: string;
  className?: string;
  placeholderClassName?: string;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [src, setSrc] = useState("");
  const [failed, setFailed] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return;

    if (typeof IntersectionObserver === "undefined") {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "600px" },
    );
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;

    setFailed(false);
    setSrc("");
    if (!shouldLoad) return;

    const timer = setTimeout(() => {
      void resolveImportPhotoThumbnailUrlQueued(photo)
        .then((url) => {
          if (!cancelled) setSrc(url);
        })
        .catch((error) => {
          console.warn("Failed to resolve import photo URL", { photo, error });
          if (!cancelled) setFailed(true);
        });
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [photo, shouldLoad]);

  if (!src || failed) {
    return (
      <div ref={rootRef} className={`flex items-center justify-center rounded bg-muted/40 text-xs text-muted-foreground ${placeholderClassName}`}>
        {failed ? "Unable to load photo" : shouldLoad ? "Loading thumbnail..." : "Photo"}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      className={className}
      onError={() => setFailed(true)}
    />
  );
}

function buildUpdatedStagedPartial(current: Partial<Job>, patch: Partial<Job>): Partial<Job> {
  const next: Partial<Job> = {
    ...current,
    ...patch,
  };
  next.sourceMeta = {
    ...(current.sourceMeta ?? {
      sourceSystem: "jobber",
      readinessMode: "external_managed",
      externalProjectId: current.id ?? "unknown",
      importedAt: new Date().toISOString(),
      importBatchId: "manual",
      migrationStatus: "needs_review",
      reviewFlags: [],
    }),
    // Merge any sourceMeta fields from the patch (e.g. bypassedStages)
    ...(patch.sourceMeta ?? {}),
    reviewFlags: computeReviewFlags(next),
  };
  return next;
}

function toDbImportJob(partial: Partial<Job>, customerId: string): Job {
  const address = partial.address ?? { street: "", city: "", state: "", zip: "" };
  return {
    id: partial.id ?? "",
    jobNumber: partial.jobNumber ?? "",
    customerName: partial.customerName ?? "Unknown Customer",
    address,
    phone: partial.phone ?? "",
    email: partial.email ?? "",
    secondaryContactName: partial.secondaryContactName ?? "",
    secondaryContactPhone: partial.secondaryContactPhone ?? "",
    secondaryContactEmail: partial.secondaryContactEmail ?? "",
    projectType: partial.projectType ?? "Other",
    assignedCrew: partial.assignedCrew ?? "",
    crewId: partial.crewId,
    assignmentType: partial.assignmentType ?? "Unassigned",
    scheduledInstallDate: partial.scheduledInstallDate ?? "",
    timeSlot: partial.timeSlot,
    permitRequired: partial.permitRequired ?? false,
    permitStatus: partial.permitStatus ?? "Not Required",
    orderStatus: partial.orderStatus ?? "Not Ordered",
    materialsStatus: partial.materialsStatus ?? "Not Ready",
    dispatchStatus: partial.dispatchStatus ?? "Not Ready",
    contractorAssignmentStatus: partial.contractorAssignmentStatus ?? "",
    currentStage: partial.currentStage ?? "Sale Closed",
    notes: partial.notes ?? "",
    issueFlag: partial.issueFlag ?? false,
    scopeSummary: partial.scopeSummary ?? "",
    description: partial.description ?? "",
    payoutAmount: partial.payoutAmount ?? 0,
    assignedSubcontractor: partial.assignedSubcontractor ?? "",
    assignedSubcontractorId: partial.assignedSubcontractorId,
    targetInstallDate: partial.targetInstallDate ?? "",
    activityHistory: partial.activityHistory ?? [],
    photos: partial.photos ?? [],
    photoGroups: partial.photoGroups,
    qaSubmitted: partial.qaSubmitted ?? false,
    materialReadiness: partial.materialReadiness ?? "Not Ordered",
    materialReadinessNotes: partial.materialReadinessNotes ?? "",
    missingItemsList: partial.missingItemsList ?? "",
    procurementStatus: partial.procurementStatus ?? "Not Ordered",
    warehouseStatus: partial.warehouseStatus ?? "Not Received",
    branch: partial.branch ?? "",
    region: partial.region ?? "",
    salesRep: partial.salesRep ?? "",
    permitDetail: partial.permitDetail,
    orderDetail: partial.orderDetail,
    closeoutStatus: partial.closeoutStatus ?? "Not Started",
    finalDocumentationComplete: partial.finalDocumentationComplete ?? false,
    customerSignoff: partial.customerSignoff ?? false,
    closeoutNotes: partial.closeoutNotes ?? "",
    materialsList: partial.materialsList,
    unmappedSkusForReview: partial.unmappedSkusForReview,
    financials: partial.financials,
    marketing: partial.marketing,
    externalRef: partial.externalRef,
    origin: partial.origin,
    scheduledStartTime: partial.scheduledStartTime,
    scheduledEndTime: partial.scheduledEndTime,
    timezone: partial.timezone,
    externalCalendarEventId: partial.externalCalendarEventId,
    projectConfig: partial.projectConfig,
    rescissionStatus: partial.rescissionStatus ?? "Not Applicable",
    rescissionEndsAt: partial.rescissionEndsAt,
    convertedFromQuoteId: partial.convertedFromQuoteId,
    boundJobSpec: partial.boundJobSpec,
    executionReady: partial.executionReady,
    contractDocument: partial.contractDocument,
    changeOrders: partial.changeOrders,
    scopeLocked: partial.scopeLocked ?? false,
    opportunityId: partial.opportunityId,
    appointmentResultId: partial.appointmentResultId,
    intakeApproved: partial.intakeApproved ?? false,
    approvedForOrdering: partial.approvedForOrdering ?? false,
    holdStatus: partial.holdStatus,
    holdReason: partial.holdReason,
    planningStatus: partial.planningStatus,
    orderType: partial.orderType,
    selectedVendor: partial.selectedVendor,
    selectedVendorType: partial.selectedVendorType,
    schedulingStatus: partial.schedulingStatus,
    installReadiness: partial.installReadiness,
    jobMaterialStatus: partial.jobMaterialStatus,
    installStatus: partial.installStatus,
    qaStatus: partial.qaStatus,
    reviewSent: partial.reviewSent ?? false,
    reviewCompleted: partial.reviewCompleted ?? false,
    reviewSatisfied: partial.reviewSatisfied ?? null,
    reviewSentAt: partial.reviewSentAt,
    referralRequested: partial.referralRequested ?? false,
    referralReceived: partial.referralReceived ?? false,
    referralRequestedAt: partial.referralRequestedAt,
    googleReviewUrl: partial.googleReviewUrl,
    siteReadiness: partial.siteReadiness,
    buildReviewApproved: partial.buildReviewApproved,
    buildReviewStatus: partial.buildReviewStatus,
    tenantId: partial.tenantId,
    planningReview: partial.planningReview,
    ownerContractorId: partial.ownerContractorId,
    installerChange: partial.installerChange,
    sourceMeta: partial.sourceMeta,
    migrationImportDetail: partial.migrationImportDetail,
    // internal bridge for JobService.resolveCustomerIdForJob()
    ...( { customerId } as unknown as Pick<Job, never> ),
  };
}

type CsvPreviewRow = {
  projectId: string;
  customerName: string;
  address: string;
  installType: string;
  startAt: string;
  endAt: string;
  contractor: string;
  quoteNumber: string;
  total: number;
  warnings: string[];
  isDuplicate: boolean;
};

function buildCsvPreview(text: string, existingKeys: Set<string>): CsvPreviewRow[] {
  const rows = parseCsv(text);
  const seenIds = new Set<string>();
  return rows.map((row) => {
    const projectId = String(row.project_id ?? row.id ?? "").trim();
    const customerName = String(row.customer_name ?? row.client_name ?? "").trim();
    const street = String(row.street ?? row.address ?? "").trim();
    const city = String(row.city ?? "").trim();
    const state = String(row.state ?? "").trim();
    const installType = String(row.install_type ?? row.project_type ?? "").trim();
    const startAt = String(row.start_at ?? "").trim();
    const endAt = String(row.end_at ?? row.scheduled_end_at ?? "").trim();
    const contractor = String(row.contractor ?? row.sales_rep ?? row.assigned_rep ?? "").trim();
    const quoteNumber = String(row.quote_number ?? row.quote_id ?? "").trim();
    const total = Number(row.total ?? row.contract_value ?? 0) || 0;
    const address = [street, city, state].filter(Boolean).join(", ");

    const warnings: string[] = [];
    if (!customerName) warnings.push("Missing customer name");
    if (!projectId) warnings.push("No project ID — one will be auto-generated");
    if (!installType) warnings.push("No install type — defaults to Other");
    if (!total) warnings.push("Missing contract total");

    const key = jobKey("jobber", projectId || `preview-${Math.random()}`);
    const isDuplicate = Boolean(projectId && (existingKeys.has(key) || seenIds.has(projectId)));
    if (projectId) seenIds.add(projectId);

    return { projectId, customerName, address, installType, startAt, endAt, contractor, quoteNumber, total, warnings, isDuplicate };
  });
}

const MIGRATION_CSV_TEMPLATE =
  "project_id,customer_name,phone,email,street,city,state,zip,install_type,scope,start_at,end_at,total,contractor,signature_date,notes,quote_number\n" +
  "JOB-001,Jane Doe,8135550100,jane@example.com,123 Main St,Tampa,FL,33602,Shower Remodel,Master Shower replacement,2026-06-15,2026-06-17,8500,Creative Concepts Contracting,2026-05-01,,5303\n";

function downloadMigrationTemplate() {
  const blob = new Blob([MIGRATION_CSV_TEMPLATE], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "koredor-migration-template.csv";
  link.click();
  URL.revokeObjectURL(url);
}

export default function MigrationReviewQueue() {
  const navigate = useNavigate();
  const { jobs, refreshData } = useAppData();
  const [csvText, setCsvText] = useState("");
  const [csvFileName, setCsvFileName] = useState("");
  const [isCsvReading, setIsCsvReading] = useState(false);
  const [jsonText, setJsonText] = useState("");
  const [isExcelBusy, setIsExcelBusy] = useState(false);
  const [manualContractFile, setManualContractFile] = useState<File | null>(null);
  const [manualContractCustomerName, setManualContractCustomerName] = useState("");
  const [manualContractProjectId, setManualContractProjectId] = useState("");
  const [manualContractProjectType, setManualContractProjectType] = useState<ProjectType>("Full Bathroom Remodel");
  const [manualContractEmail, setManualContractEmail] = useState("");
  const [manualContractPhone, setManualContractPhone] = useState("");
  const [manualContractAddressLine1, setManualContractAddressLine1] = useState("");
  const [manualContractCity, setManualContractCity] = useState("");
  const [manualContractState, setManualContractState] = useState("");
  const [manualContractPostalCode, setManualContractPostalCode] = useState("");
  const [manualContractAmount, setManualContractAmount] = useState("");
  const [manualContractQuoteNumber, setManualContractQuoteNumber] = useState("");
  const [manualContractSignedDate, setManualContractSignedDate] = useState("");
  const [manualContractPreview, setManualContractPreview] = useState<MigrationContractPreviewDraft | null>(null);
  const [manualContractAiScopeDraft, setManualContractAiScopeDraft] = useState<MigrationAiScopeDraft | null>(null);
  const [isManualContractPreviewBusy, setIsManualContractPreviewBusy] = useState(false);
  const [isManualContractBusy, setIsManualContractBusy] = useState(false);
  const [stagedImports, setStagedImports] = useState<StagedImport[]>([]);
  const [busyImportRowId, setBusyImportRowId] = useState<string | null>(null);
  const [generatingScopeRowId, setGeneratingScopeRowId] = useState<string | null>(null);
  const [scopeDraftDialog, setScopeDraftDialog] = useState<ScopeDraftDialogState | null>(null);
  const [catalogProducts, setCatalogProducts] = useState<RegistryProduct[]>([]);
  const [catalogProductsLoading, setCatalogProductsLoading] = useState(false);
  const [quickRegisterOpen, setQuickRegisterOpen] = useState(false);
  const [quickRegisterPrefill, setQuickRegisterPrefill] = useState<{ name?: string; sku?: string }>({});
  const { role } = useRole();
  const CAN_REGISTER_PRODUCT = new Set(['super_admin', 'admin', 'owner', 'ops_manager']);
  const canRegisterProduct = CAN_REGISTER_PRODUCT.has(role ?? '');
  const [activeSkuMatcherId, setActiveSkuMatcherId] = useState<string | null>(null);
  const [photoPageByRow, setPhotoPageByRow] = useState<Record<string, number>>({});
  const [groupedPhotoUploadsEnabledByRow, setGroupedPhotoUploadsEnabledByRow] = useState<Record<string, boolean>>({});
  const [newPhotoGroupLabelByRow, setNewPhotoGroupLabelByRow] = useState<Record<string, string>>({});
  const [selectedJobKey, setSelectedJobKey] = useState<string | null>(null);
  const [postApproveJob, setPostApproveJob] = useState<Job | null>(null);
  const [photoGallery, setPhotoGallery] = useState<PhotoGalleryState | null>(null);
  const [photoGalleryUrl, setPhotoGalleryUrl] = useState("");
  const [photoGalleryLoading, setPhotoGalleryLoading] = useState(false);
  const [photoGalleryError, setPhotoGalleryError] = useState("");
  const [galleryThumbnailStartByPhoto, setGalleryThumbnailStartByPhoto] = useState<Record<string, number>>({});
  const [uploadStatusByKey, setUploadStatusByKey] = useState<
    Record<string, UploadEntry>
  >({});
  const [uploadBatchByScope, setUploadBatchByScope] = useState<Record<string, UploadBatch>>({});
  const materialsDebounceByRow = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const activeGalleryPhoto = photoGallery?.photos[photoGallery.index] ?? "";
  const galleryThumbnailKey = photoGallery?.photos.join("|") ?? "";
  const maxGalleryThumbnailStart = photoGallery
    ? Math.max(photoGallery.photos.length - GALLERY_THUMBNAIL_LIMIT, 0)
    : 0;
  const galleryThumbnailStart = photoGallery
    ? Math.min(galleryThumbnailStartByPhoto[galleryThumbnailKey] ?? 0, maxGalleryThumbnailStart)
    : 0;
  const galleryThumbnailItems = photoGallery
    ? photoGallery.photos
        .slice(galleryThumbnailStart, galleryThumbnailStart + GALLERY_THUMBNAIL_LIMIT)
        .map((photo, offset) => ({ photo, index: galleryThumbnailStart + offset }))
    : [];
  const shouldLoadSkuCatalog = Boolean(selectedJobKey || scopeDraftDialog);

  useEffect(() => {
    if (!shouldLoadSkuCatalog || catalogProducts.length > 0) return;
    let active = true;
    setCatalogProductsLoading(true);
    void getProductsForSkuMatching()
      .then((products) => {
        if (!active) return;
        setCatalogProducts(products.filter((product) => product.active && normalizeMaterialSku(product.sku)));
      })
      .catch((error) => {
        console.error("[MigrationReviewQueue] Failed to load product registry for SKU matching:", error);
        if (active) toast.error("Failed to load Product Registry SKUs");
      })
      .finally(() => {
        if (active) setCatalogProductsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [catalogProducts.length, shouldLoadSkuCatalog]);

  const catalogProductBySku = useMemo(() => {
    const map = new Map<string, RegistryProduct>();
    catalogProducts.forEach((product) => {
      map.set(normalizeMaterialSku(product.sku), product);
    });
    return map;
  }, [catalogProducts]);

  useEffect(() => {
    let cancelled = false;

    setPhotoGalleryLoading(Boolean(activeGalleryPhoto));
    setPhotoGalleryError("");
    if (!activeGalleryPhoto) return;

    void resolveImportPhotoUrl(activeGalleryPhoto)
      .then((url) => {
        if (!cancelled) setPhotoGalleryUrl(url);
      })
      .catch((error) => {
        console.warn("Failed to resolve import photo gallery URL", { photo: activeGalleryPhoto, error });
        if (!cancelled) {
          setPhotoGalleryError("Could not load this photo. Try refreshing.");
          setPhotoGalleryLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeGalleryPhoto]);

  const setFileStatus = (key: string, status: UploadEntry) => {
    setUploadStatusByKey((prev) => {
      return { ...prev, [key]: status };
    });
  };

  const setFileUploadProgress = (key: string, fraction: number) => {
    setUploadStatusByKey((prev) => ({
      ...prev,
      [key]: { status: "uploading", fraction },
    }));
  };

  const setUploadBatch = (rowId: string, groupId: string | undefined, total: number) => {
    setUploadBatchByScope((prev) => ({
      ...prev,
      [uploadScopeKey(rowId, groupId)]: { total },
    }));
  };

  const isFileBusy = (rowId: string, file: File, groupId?: string) => {
    const key = uploadKey(rowId, file, groupId);
    const s = uploadStatusByKey[key];
    return !!s && s.status !== "done" && s.status !== "error";
  };

  const getFileStatus = (rowId: string, file: File, groupId?: string) => uploadStatusByKey[uploadKey(rowId, file, groupId)];

  const clearSettledUploadStatuses = (rowId: string, groupId?: string, delayMs = 1600) => {
    const prefix = `upload:${rowId}:${groupId ?? UNGROUPED_UPLOAD_GROUP}:`;
    const scopeKey = uploadScopeKey(rowId, groupId);
    window.setTimeout(() => {
      setUploadStatusByKey((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(next)) {
          if (
            k.startsWith(prefix) &&
            (next[k].status === "done" || next[k].status === "error")
          ) {
            delete next[k];
          }
        }
        return next;
      });
      setUploadBatchByScope((prev) => {
        const next = { ...prev };
        delete next[scopeKey];
        return next;
      });
    }, delayMs);
  };

  const loadStagedImports = async () => {
    const orgId = await getCurrentOrganizationId();
    if (!orgId || orgId === "pending-setup") {
      setStagedImports([]);
      return;
    }
    const { data, error } = await supabase
      .from("job_import_rows")
      .select("id, batch_id, payload, created_at")
      .eq("organization_id", orgId)
      .eq("status", "staged")
      .order("created_at", { ascending: false });
    if (error) {
      throw error;
    }
    const rows = (data ?? []) as Array<{
      id: string;
      batch_id: string;
      payload: Partial<Job>;
      created_at: string;
    }>;
    setStagedImports(
      rows.map((row) => ({
        id: row.id,
        batchId: row.batch_id,
        partial: row.payload ?? {},
        importedAt: row.created_at,
      })),
    );
  };

  useEffect(() => {
    void loadStagedImports().catch(() => {});
  }, []);

  const pendingPersisted = useMemo(() => filterPendingReview(jobs), [jobs]);
  const pending = useMemo<Job[]>(
    () => [
      ...stagedImports.map((row) => toDbImportJob(row.partial, "")),
      ...pendingPersisted,
    ],
    [stagedImports, pendingPersisted],
  );

  const existingImportedKeys = useMemo(() => {
    const set = new Set<string>();
    for (const job of jobs) {
      const source = job.sourceMeta?.sourceSystem;
      const ext = job.sourceMeta?.externalProjectId;
      if (source && ext) set.add(jobKey(source, ext));
    }
    for (const row of stagedImports) {
      const source = row.partial.sourceMeta?.sourceSystem;
      const ext = row.partial.sourceMeta?.externalProjectId;
      if (source && ext) set.add(jobKey(source, ext));
    }
    return set;
  }, [jobs, stagedImports]);

  const csvPreviewRows = useMemo<CsvPreviewRow[] | null>(() => {
    if (!csvText.trim()) return null;
    try { return buildCsvPreview(csvText, existingImportedKeys); } catch { return null; }
  }, [csvText, existingImportedKeys]);

  const findStagedRowForJob = (job: Job): StagedImport | undefined =>
    stagedImports.find(
      (row) =>
        row.partial.sourceMeta?.sourceSystem === job.sourceMeta?.sourceSystem &&
        row.partial.sourceMeta?.externalProjectId === job.sourceMeta?.externalProjectId,
    );

  const getJobQueueKey = (job: Job): string => findStagedRowForJob(job)?.id ?? job.id;

  const updateLocalStagedImport = (rowId: string, patch: Partial<Job>) => {
    setStagedImports((rows) =>
      rows.map((row) =>
        row.id === rowId
          ? { ...row, partial: buildUpdatedStagedPartial(row.partial, patch) }
          : row,
      ),
    );
  };

  const saveStagedImport = async (rowId: string, partialOverride?: Partial<Job>, options?: { silent?: boolean }) => {
    const row = stagedImports.find((candidate) => candidate.id === rowId);
    const partial = partialOverride ?? row?.partial;
    if (!partial) return false;

    setBusyImportRowId(rowId);
    const next = buildUpdatedStagedPartial(partial, {});
    const { error } = await supabase
      .from("job_import_rows")
      .update({
        payload: next,
        customer_name: next.customerName ?? null,
        updated_at: new Date().toISOString(),
      })
      .eq("id", rowId);
    setBusyImportRowId(null);

    if (error) {
      toast.error("Failed to save import edits", { description: error.message });
      return false;
    }

    setStagedImports((rows) =>
      rows.map((candidate) =>
        candidate.id === rowId ? { ...candidate, partial: next } : candidate,
      ),
    );
    if (!options?.silent) {
      toast.success("Import review edits saved.");
    }
    return true;
  };

  const uploadImportPhotoFiles = async (rowId: string, files: File[], groupId?: string): Promise<string[]> => {
    const orgId = await getCurrentOrganizationId();
    if (!orgId || orgId === "pending-setup") {
      throw new Error("Missing organization context for photo upload.");
    }
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    if (sessionError || !accessToken) {
      throw new Error("You must be signed in before uploading import photos.");
    }

    const uploadedPaths: string[] = [];
    for (const file of files) {
      const fileKey = uploadKey(rowId, file, groupId);
      setFileStatus(fileKey, { status: "hashing" });

      const hash = await computeFileHash(file);
      if (isSessionDuplicate(hash)) {
        toast.warning(`Duplicate photo skipped: "${file.name}" was already uploaded in this session.`);
        setFileStatus(fileKey, { status: "error" });
        continue;
      }

      const safeName = sanitizeStorageFileName(file.name);
      const uniqueId =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const storagePath = `${orgId}/${rowId}/${uniqueId}-${safeName}`;
      const uploadUrl = `${supabaseUrl}/storage/v1/object/${IMPORT_PHOTO_BUCKET}/${encodeStoragePath(storagePath)}`;

      setFileStatus(fileKey, { status: "uploading", fraction: 0 });
      try {
        await uploadWithXhr({
          url: uploadUrl,
          token: accessToken,
          apiKey: supabaseAnonKey,
          file,
          contentType: file.type || "image/jpeg",
          cacheControl: "3600",
          onProgress: (fraction) => setFileUploadProgress(fileKey, fraction),
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setFileStatus(fileKey, { status: "error", error: message });
        throw err;
      }

      setFileStatus(fileKey, { status: "saving" });
      registerUploadHash(hash);
      uploadedPaths.push(storagePath);
      setFileStatus(fileKey, { status: "done" });
    }

    return uploadedPaths;
  };

  const removeImportPhotoObject = async (photoRef: string) => {
    const storagePath = extractImportPhotoStoragePath(photoRef);
    if (!storagePath) return;

    const { error } = await supabase.storage.from(IMPORT_PHOTO_BUCKET).remove([storagePath]);
    if (error) {
      console.warn("Failed to remove import photo object", { storagePath, error });
    }
  };

  const uploadImportPhotos = async (row: StagedImport, files: FileList | null, groupId?: string) => {
    const selectedFiles = Array.from(files ?? []).filter((file) => file.type.startsWith("image/"));
    if (selectedFiles.length === 0) return;

    setUploadBatch(row.id, groupId, selectedFiles.length);
    setBusyImportRowId(row.id);
    try {
      const uploadedPaths = await uploadImportPhotoFiles(row.id, selectedFiles, groupId);
      const currentPhotos = row.partial.photos ?? [];
      const nextPhotos = [...currentPhotos, ...uploadedPaths];
      const currentGroups = normalizePhotoGroups(row.partial);
      const nextGroups = groupId
        ? appendUploadedPhotosToGroup(currentGroups, groupId, uploadedPaths)
        : currentGroups;
      const next = buildUpdatedStagedPartial(row.partial, withPhotoGroups({ ...row.partial, photos: nextPhotos }, nextGroups));
      const saved = await saveStagedImport(row.id, next, { silent: true });
      if (saved) {
        toast.success(`Uploaded ${uploadedPaths.length} job photo${uploadedPaths.length === 1 ? "" : "s"}.`);
      }
    } catch (err) {
      toast.error("Photo upload failed", {
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setBusyImportRowId(null);
      clearSettledUploadStatuses(row.id, groupId);
    }
  };

  const removeImportPhoto = async (row: StagedImport, photoRef: string, groupId?: string) => {
    const photos = row.partial.photos ?? [];
    if (!photos.includes(photoRef)) return;

    setBusyImportRowId(row.id);
    try {
      const nextPhotos = photos.filter((photo) => photo !== photoRef);
      const currentGroups = normalizePhotoGroups(row.partial);
      const nextGroups = groupId
        ? removePhotoRefFromGroup(currentGroups, groupId, photoRef)
        : currentGroups;
      const next = buildUpdatedStagedPartial(row.partial, withPhotoGroups({ ...row.partial, photos: nextPhotos }, nextGroups));
      const saved = await saveStagedImport(row.id, next, { silent: true });
      if (saved) {
        await removeImportPhotoObject(photoRef);
        toast.success("Removed job photo.");
      }
    } catch (err) {
      toast.error("Photo remove failed", {
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setBusyImportRowId(null);
      clearSettledUploadStatuses(row.id, groupId);
    }
  };

  const replaceImportPhoto = async (row: StagedImport, photoRef: string, files: FileList | null, groupId?: string) => {
    const replacement = Array.from(files ?? []).find((file) => file.type.startsWith("image/"));
    if (!replacement) return;

    const photos = row.partial.photos ?? [];
    if (!photos.includes(photoRef)) return;

    setUploadBatch(row.id, groupId, 1);
    setBusyImportRowId(row.id);
    try {
      const [replacementPath] = await uploadImportPhotoFiles(row.id, [replacement], groupId);
      if (!replacementPath) throw new Error("Replacement upload did not return a photo path.");

      const nextPhotos = photos.map((photo) => (photo === photoRef ? replacementPath : photo));
      const currentGroups = normalizePhotoGroups(row.partial);
      const nextGroups = groupId
        ? replacePhotoRefInGroup(currentGroups, groupId, photoRef, replacementPath)
        : currentGroups;
      const next = buildUpdatedStagedPartial(row.partial, withPhotoGroups({ ...row.partial, photos: nextPhotos }, nextGroups));
      const saved = await saveStagedImport(row.id, next, { silent: true });
      if (saved) {
        await removeImportPhotoObject(photoRef);
        toast.success("Replaced job photo.");
      }
    } catch (err) {
      toast.error("Photo replace failed", {
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setBusyImportRowId(null);
      clearSettledUploadStatuses(row.id, groupId);
    }
  };

  const getCurrentProfileIdForImport = async (): Promise<string | null> => {
    const { data: authData } = await supabase.auth.getUser();
    const authUserId = authData.user?.id ?? null;
    if (!authUserId) return null;

    const { data: profile } = await supabase
      .from("profiles")
      .select("id")
      .eq("auth_user_id", authUserId)
      .maybeSingle();
    return (profile as { id?: string } | null)?.id ?? null;
  };

  const resetManualContractForm = () => {
    setManualContractFile(null);
    setManualContractCustomerName("");
    setManualContractProjectId("");
    setManualContractProjectType("Full Bathroom Remodel");
    setManualContractEmail("");
    setManualContractPhone("");
    setManualContractAddressLine1("");
    setManualContractCity("");
    setManualContractState("");
    setManualContractPostalCode("");
    setManualContractAmount("");
    setManualContractQuoteNumber("");
    setManualContractSignedDate("");
    setManualContractPreview(null);
    setManualContractAiScopeDraft(null);
  };

  const previewManualContract = async () => {
    const file = manualContractFile;
    if (!file) {
      toast.warning("Choose a contract file first.");
      return;
    }
    if (!isAllowedContractFile(file)) {
      toast.warning("Upload a PDF, DOC, or DOCX contract file.");
      return;
    }
    if (!isPdfContractDocument({ fileName: file.name, mimeType: file.type, filePath: file.name } as Job["contractDocument"])) {
      toast.warning("AI preview currently supports PDF contracts only.");
      return;
    }

    setIsManualContractPreviewBusy(true);
    try {
      const fileBase64 = await fileToBase64(file);
      const { data, error } = await supabase.functions.invoke<ImportContractPreviewResponse>(
        "import-contract-preview",
        {
          body: {
            fileName: file.name,
            mimeType: file.type || "application/pdf",
            fileBase64,
          },
        },
      );
      if (error) throw error;
      if (!data?.preview) {
        throw new Error("The AI preview did not include contract metadata.");
      }

      const preview = normalizeContractPreviewDraft(data.preview, data.model, file.name);
      const aiScopeDraft = data.generation
        ? normalizeAiScopeDraft(data.generation, data.model, file.name)
        : null;
      const preferredProjectId = preview.jobNumber || preview.quoteNumber || manualContractProjectId.trim();

      setManualContractPreview(preview);
      setManualContractAiScopeDraft(aiScopeDraft);
      setManualContractCustomerName(preview.customerName);
      setManualContractEmail(preview.customerEmail);
      setManualContractPhone(preview.customerPhone);
      setManualContractAddressLine1(preview.addressLine1);
      setManualContractCity(preview.city);
      setManualContractState(preview.state);
      setManualContractPostalCode(preview.postalCode);
      setManualContractProjectType(matchProjectType(preview.projectType));
      setManualContractQuoteNumber(preview.quoteNumber);
      setManualContractProjectId(preferredProjectId);
      setManualContractAmount(preview.contractAmount > 0 ? String(preview.contractAmount) : "");
      setManualContractSignedDate(preview.signedDate);
      toast.success("Contract preview generated. Review the fields before adding to queue.");
    } catch (err) {
      toast.error("Contract preview failed", {
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setIsManualContractPreviewBusy(false);
    }
  };

  const createQueueEntryFromContract = async () => {
    const file = manualContractFile;
    if (!file) {
      toast.warning("Choose a contract file first.");
      return;
    }
    if (!isAllowedContractFile(file)) {
      toast.warning("Upload a PDF, DOC, or DOCX contract file.");
      return;
    }
    if (isPdfContractDocument({ fileName: file.name, mimeType: file.type, filePath: file.name } as Job["contractDocument"]) && !manualContractPreview) {
      toast.warning("Generate and review the contract preview before adding it to the queue.");
      return;
    }

    setIsManualContractBusy(true);
    let storagePath = "";
    try {
      const orgId = await getCurrentOrganizationId();
      if (!orgId || orgId === "pending-setup") {
        throw new Error("Missing organization context for contract import.");
      }

      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (sessionError || !accessToken) {
        throw new Error("You must be signed in before uploading contracts.");
      }

      const rowId = randomStableId();
      const batchId = randomStableId();
      const now = new Date().toISOString();
      const fileHash = await computeFileHash(file);
      const externalProjectId =
        manualContractProjectId.trim() ||
        `CONTRACT-${now.slice(0, 10).replace(/-/g, "")}-${fileHash.slice(0, 6).toUpperCase()}-${rowId.slice(0, 6).toUpperCase()}`;
      if (manualContractProjectId.trim() && existingImportedKeys.has(jobKey("manual_import", externalProjectId))) {
        throw new Error(`A manual import already exists for "${externalProjectId}". Use a different Job / quote ID.`);
      }
      const customerName = manualContractCustomerName.trim() || "Unknown Customer";
      const contractAmount = Number(manualContractAmount || 0) || 0;
      const safeName = sanitizeStorageFileName(file.name);
      storagePath = `import-contracts/${orgId}/${rowId}/${randomStableId()}-${safeName}`;
      const uploadUrl = `${supabaseUrl}/storage/v1/object/${IMPORT_CONTRACT_BUCKET}/${encodeStoragePath(storagePath)}`;

      await uploadWithXhr({
        url: uploadUrl,
        token: accessToken,
        apiKey: supabaseAnonKey,
        file,
        contentType: file.type || "application/octet-stream",
        cacheControl: "3600",
      });

      const basePartial: Partial<Job> = {
        id: externalProjectId,
        jobNumber: `JOB-IMP-${externalProjectId}`,
        customerName,
        projectType: manualContractProjectType,
        phone: manualContractPhone.trim(),
        email: manualContractEmail.trim(),
        currentStage: "Sale Closed",
        scopeSummary: "",
        notes: "Created from a manually uploaded migration contract.",
        address: {
          street: manualContractAddressLine1.trim(),
          city: manualContractCity.trim(),
          state: manualContractState.trim(),
          zip: manualContractPostalCode.trim(),
        },
        financials: contractAmount > 0
          ? {
              contractValue: contractAmount,
              estimatedMaterialCost: 0,
              estimatedLaborCost: 0,
              subcontractorPayout: 0,
              marginEstimate: 0,
              paymentStatus: "Not Invoiced",
              payoutStatus: "Not Submitted",
            }
          : undefined,
        orderStatus: "Not Ordered",
        materialsStatus: "Not Ready",
        materialReadiness: "Not Ordered",
        procurementStatus: "Not Ordered",
        warehouseStatus: "Not Received",
        migrationImportDetail: {
          ...(manualContractPreview ? {
            aiContractPreview: normalizeContractPreviewDraft(
              {
                ...manualContractPreview,
                customerName,
                customerEmail: manualContractEmail.trim(),
                customerPhone: manualContractPhone.trim(),
                addressLine1: manualContractAddressLine1.trim(),
                city: manualContractCity.trim(),
                state: manualContractState.trim(),
                postalCode: manualContractPostalCode.trim(),
                projectType: manualContractProjectType,
                quoteNumber: manualContractQuoteNumber.trim(),
                jobNumber: manualContractProjectId.trim(),
                contractAmount,
                signedDate: manualContractSignedDate.trim(),
              },
              manualContractPreview.model,
              file.name,
            ),
          } : {}),
          ...(manualContractAiScopeDraft ? {
            aiScopeDraft: normalizeAiScopeDraft(manualContractAiScopeDraft, manualContractAiScopeDraft.model, file.name),
          } : {}),
        },
        sourceMeta: {
          sourceSystem: "manual_import",
          readinessMode: "external_managed",
          externalProjectId,
          importedAt: now,
          importBatchId: batchId,
          migrationStatus: "needs_review",
          reviewFlags: [],
          mappingConfidence: 0.35,
          rawSourceRef: `${IMPORT_CONTRACT_BUCKET}::${storagePath}`,
          // Contract-created rows default to the native operational flow.
          // Reviewers can explicitly choose migration bypasses before approval.
          bypassedStages: [],
        },
      };
      const contractDocument = {
        ...buildUploadedContractDocument(basePartial, file, storagePath),
        quoteId: manualContractQuoteNumber.trim() || externalProjectId,
        contractTotal: contractAmount,
        homeownerSignedAt: manualContractSignedDate.trim() || undefined,
        status: manualContractSignedDate.trim() ? "Signed" as const : "Pending" as const,
      };
      const finalPartial = buildUpdatedStagedPartial(
        { ...basePartial, contractDocument },
        {},
      );
      const reviewFlags = computeReviewFlags(finalPartial);

      const profileId = await getCurrentProfileIdForImport();
      const { error: batchErr } = await supabase.from("job_import_batches").insert({
        id: batchId,
        organization_id: orgId,
        source_system: "manual_import",
        import_mode: "json",
        content_hash: `manual-contract:${fileHash}`,
        rows_total: 1,
        rows_staged: 1,
        created_by: profileId,
      });
      if (batchErr) throw batchErr;

      const { error: rowErr } = await supabase.from("job_import_rows").insert({
        id: rowId,
        batch_id: batchId,
        organization_id: orgId,
        source_system: "manual_import",
        external_project_id: externalProjectId,
        customer_name: customerName,
        payload: finalPartial,
        review_flags: reviewFlags,
        status: "staged",
      });
      if (rowErr) throw rowErr;

      const stagedRow: StagedImport = {
        id: rowId,
        batchId,
        partial: finalPartial,
        importedAt: now,
      };
      setStagedImports((rows) => [stagedRow, ...rows]);
      resetManualContractForm();
      setSelectedJobKey(rowId);
      toast.success("Contract staged as a new import queue entry.");
    } catch (err) {
      if (storagePath) {
        const { error } = await supabase.storage.from(IMPORT_CONTRACT_BUCKET).remove([storagePath]);
        if (error) {
          console.warn("Failed to remove contract object after staging failure", { storagePath, error });
        }
      }
      toast.error("Contract queue entry failed", {
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setIsManualContractBusy(false);
    }
  };

  const uploadImportContract = async (row: StagedImport, files: FileList | null) => {
    const file = Array.from(files ?? [])[0];
    if (!file) return;

    if (!isAllowedContractFile(file)) {
      toast.warning("Upload a PDF, DOC, or DOCX contract file.");
      return;
    }

    setBusyImportRowId(row.id);
    try {
      const orgId = await getCurrentOrganizationId();
      if (!orgId || orgId === "pending-setup") {
        throw new Error("Missing organization context for contract upload.");
      }
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (sessionError || !accessToken) {
        throw new Error("You must be signed in before uploading contracts.");
      }

      const safeName = sanitizeStorageFileName(file.name);
      const storagePath = `import-contracts/${orgId}/${row.id}/${randomStableId()}-${safeName}`;
      const uploadUrl = `${supabaseUrl}/storage/v1/object/${IMPORT_CONTRACT_BUCKET}/${encodeStoragePath(storagePath)}`;
      await uploadWithXhr({
        url: uploadUrl,
        token: accessToken,
        apiKey: supabaseAnonKey,
        file,
        contentType: file.type || "application/octet-stream",
        cacheControl: "3600",
      });

      const existingPath = normalizeContractStoragePath(row.partial.contractDocument?.filePath);
      const nextContract = buildUploadedContractDocument(row.partial, file, storagePath);
      const next = buildUpdatedStagedPartial(row.partial, { contractDocument: nextContract });
      const saved = await saveStagedImport(row.id, next, { silent: true });
      if (saved) {
        if (existingPath && existingPath !== storagePath) {
          const { error } = await supabase.storage.from(IMPORT_CONTRACT_BUCKET).remove([existingPath]);
          if (error) {
            console.warn("Failed to remove replaced import contract object", { existingPath, error });
          }
        }
        toast.success("Contract uploaded for this staged job.");
      }
    } catch (err) {
      toast.error("Contract upload failed", {
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setBusyImportRowId(null);
    }
  };

  const removeImportContract = async (row: StagedImport) => {
    const contract = row.partial.contractDocument;
    if (!contract?.filePath) return;

    setBusyImportRowId(row.id);
    try {
      const storagePath = normalizeContractStoragePath(contract.filePath);
      const nextContract = {
        ...contract,
        filePath: undefined,
        fileName: undefined,
        mimeType: undefined,
        fileSize: undefined,
        uploadedAt: undefined,
        attachmentId: undefined,
      };
      const next = buildUpdatedStagedPartial(row.partial, { contractDocument: nextContract });
      const saved = await saveStagedImport(row.id, next, { silent: true });
      if (saved && storagePath) {
        const { error } = await supabase.storage.from(IMPORT_CONTRACT_BUCKET).remove([storagePath]);
        if (error) {
          console.warn("Failed to remove import contract object", { storagePath, error });
        }
        toast.success("Contract upload removed.");
      }
    } catch (err) {
      toast.error("Contract remove failed", {
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setBusyImportRowId(null);
    }
  };

  const viewImportContract = async (contract: Job["contractDocument"]) => {
    const storagePath = normalizeContractStoragePath(contract?.filePath);
    if (!storagePath) {
      toast.warning("No contract file uploaded yet.");
      return;
    }

    try {
      const signedUrl = await createSignedStorageFullImageUrl(storagePath, {
        bucket: IMPORT_CONTRACT_BUCKET,
        expiresIn: 60 * 10,
      });
      if (!signedUrl) {
        throw new Error("Missing signed URL.");
      }
      window.open(signedUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error("Could not open contract", {
        description: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const generateInstallerScope = async (row: StagedImport, options?: { force?: boolean }) => {
    const contract = row.partial.contractDocument;
    if (!contract?.filePath) {
      toast.warning("Upload a PDF contract before generating scope.");
      return;
    }
    if (!isPdfContractDocument(contract)) {
      toast.warning("AI scope generation currently supports PDF contracts only.");
      return;
    }

    const cachedDraft = row.partial.migrationImportDetail?.aiScopeDraft;
    if (!options?.force && isCachedScopeDraftForContract(cachedDraft, contract)) {
      const generation = normalizeAiScopeDraft(cachedDraft, cachedDraft.model, contract.fileName);
      setScopeDraftDialog({
        rowId: row.id,
        generation,
        selectedMaterialHintIndexes: defaultSelectedMaterialHintIndexes(generation),
        model: cachedDraft.model,
      });
      toast.message("Loaded cached AI scope draft.");
      return;
    }

    setGeneratingScopeRowId(row.id);
    try {
      const { data, error } = await supabase.functions.invoke<ImportScopeGenerationResponse>(
        "import-scope-generate",
        {
          body: {
            jobImportRowId: row.id,
            organizationId: row.partial.organizationId,
          },
        },
      );
      if (error) throw error;
      if (!data?.generation?.scopeSummary) {
        throw new Error("The AI response did not include a usable scope.");
      }

      const generation = normalizeAiScopeDraft(data.generation, data.model, contract.fileName);
      const migrationImportDetail = {
        ...(row.partial.migrationImportDetail ?? {}),
        aiScopeDraft: generation,
      };
      const next = buildUpdatedStagedPartial(row.partial, { migrationImportDetail });
      await saveStagedImport(row.id, next, { silent: true });

      setScopeDraftDialog({
        rowId: row.id,
        generation,
        selectedMaterialHintIndexes: defaultSelectedMaterialHintIndexes(generation),
        model: data.model,
        usage: data.usage,
      });
    } catch (err) {
      toast.error("Scope generation failed", {
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setGeneratingScopeRowId(null);
    }
  };

  const applyGeneratedScopeDraft = async () => {
    if (!scopeDraftDialog) return;
    const row = stagedImports.find((candidate) => candidate.id === scopeDraftDialog.rowId);
    if (!row) {
      toast.error("Could not find the staged import row for this draft.");
      setScopeDraftDialog(null);
      return;
    }

    const contractFileName = row.partial.contractDocument?.fileName;
    const aiScopeDraft = normalizeAiScopeDraft(
      scopeDraftDialog.generation,
      scopeDraftDialog.model ?? scopeDraftDialog.generation.model,
      contractFileName,
    );
    const migrationImportDetail = {
      ...(row.partial.migrationImportDetail ?? {}),
      aiScopeDraft,
    };
    const selectedIndexSet = new Set(scopeDraftDialog.selectedMaterialHintIndexes);
    const selectedMaterialHints = aiScopeDraft.extractedMaterials
      .map(normalizeAiMaterialHint)
      .filter((material, index) => selectedIndexSet.has(index) && (material.itemName || material.sku));
    const mergedMaterials = selectedMaterialHints.length > 0
      ? mergeMaterialHintsIntoMaterials(row.partial.materialsList ?? [], selectedMaterialHints, catalogProductBySku)
      : row.partial.materialsList;
    const patch: Partial<Job> = {
      scopeSummary: aiScopeDraft.scopeSummary,
      materialsList: mergedMaterials,
      migrationImportDetail,
    };
    const next = buildUpdatedStagedPartial(row.partial, patch);

    const saved = await saveStagedImport(row.id, next, { silent: true });
    if (saved) {
      toast.success("AI installer scope applied to this staged job.");
      setScopeDraftDialog(null);
    }
  };

  const persistImportedContractAttachment = async (jobId: string, contract: Job["contractDocument"]) => {
    const storagePath = normalizeContractStoragePath(contract?.filePath);
    if (!storagePath) return;

    const { data: authData } = await supabase.auth.getUser();
    const createdBy = authData.user?.id ?? "migration-import";
    const { error } = await supabase.from("attachments").insert({
      parent_type: "job",
      parent_id: jobId,
      attachment_type: "contract",
      file_name: contract?.fileName ?? "Imported contract",
      file_path: `${IMPORT_CONTRACT_BUCKET}::${storagePath}`,
      mime_type: contract?.mimeType ?? "application/octet-stream",
      file_size: contract?.fileSize ?? null,
      created_by: createdBy,
      notes: "Imported contract uploaded during migration review.",
    });
    if (error) throw error;
  };

  const persistImportedJob = async (partial: Partial<Job>): Promise<{ ok: true; jobId: string } | { ok: false; reason: string }> => {
    try {
      const { first, last } = splitName(partial.customerName ?? "");
      const email = partial.email?.trim() || null;
      const phone = partial.phone?.trim() || null;

      let customerId: string | null = null;
      if (email) {
        const byEmail = await CustomerService.getByField("email", email);
        customerId = byEmail.data?.[0]?.id ?? null;
      }
      if (!customerId && phone) {
        const byPhone = await CustomerService.getByField("phone", phone);
        customerId = byPhone.data?.[0]?.id ?? null;
      }
      if (!customerId) {
        const created = await CustomerService.create({
          first_name: first,
          last_name: last || null,
          email,
          phone,
          address_line1: partial.address?.street || null,
          city: partial.address?.city || null,
          state: partial.address?.state || null,
          postal_code: partial.address?.zip || null,
        });
        if (created.error || !created.data?.id) {
          return { ok: false, reason: `Customer create failed: ${created.error ?? "missing customer id"}` };
        }
        customerId = created.data.id as string;
      }

      const rawJobNumber = String(partial.jobNumber ?? "").trim();
      const prefixedJobNumber =
        rawJobNumber && !rawJobNumber.toUpperCase().startsWith("JOB-IMP-")
          ? `JOB-IMP-${rawJobNumber}`
          : rawJobNumber;

      const approvedPartial: Partial<Job> = {
        ...partial,
        jobNumber: prefixedJobNumber || undefined,
        scheduledInstallDate: normalizeImportedDate(partial.scheduledInstallDate),
        targetInstallDate: normalizeImportedDate(partial.targetInstallDate || partial.scheduledInstallDate),
        materialsList: partial.materialsList ? normalizeImportedMaterialLines(partial.materialsList) : partial.materialsList,
        // materialReadiness reflects the ordering bypass choice.
        // If ordering is bypassed (or bypassedStages not set = legacy all-bypass),
        // mark as "Ordered" so the ordering gate never blocks this job.
        // If ordering is NOT bypassed, start as "Not Ordered" for the normal flow.
        materialReadiness: (() => {
          const stages = partial.sourceMeta?.bypassedStages;
          if (!stages || stages.includes("ordering")) return "Ordered";
          return "Not Ordered";
        })(),
        sourceMeta: {
          ...(partial.sourceMeta ?? {
            sourceSystem: "jobber",
            readinessMode: "external_managed",
            externalProjectId: partial.id ?? "unknown",
            importedAt: new Date().toISOString(),
            importBatchId: "manual",
            migrationStatus: "needs_review",
            reviewFlags: [],
          }),
          readinessMode: "external_managed",
          migrationStatus: "approved",
          approvedAt: new Date().toISOString(),
          approvedBy: "operator",
          reviewFlags: computeReviewFlags(partial),
          rejectedReason: undefined,
        },
      };

      const dbJob = toDbImportJob(approvedPartial, customerId);
      const { data, error } = await JobService.create(dbJob as Job);
      if (error || !data?.id) {
        const msg = error instanceof Error ? error.message : String(error);
        return { ok: false, reason: `Job create failed: ${msg}` };
      }

      try {
        await persistImportedContractAttachment(data.id, approvedPartial.contractDocument);
      } catch (err) {
        console.warn("[MigrationReview] imported contract attachment persist failed (non-fatal):", err);
      }

      // Seed job_materials table so the ordering/warehouse system can see these materials.
      // materialsList lives in jobs.metadata but job_materials is the authoritative table
      // for readiness tracking, ordering, and install-readiness views.
      const rawMaterials = approvedPartial.materialsList ?? [];
      if (rawMaterials.length > 0) {
        const normalizedMaterials = normalizeImportedMaterialLines(rawMaterials);

        try {
          await replaceJobMaterialsSnapshot({
            id: data.id,
            jobNumber: dbJob.jobNumber,
            customerName: dbJob.customerName,
            currentStage: dbJob.currentStage,
            materialsList: normalizedMaterials,
          });
        } catch (err) {
          console.warn("[MigrationReview] replaceJobMaterialsSnapshot failed (non-fatal):", err);
        }
      }

      return { ok: true, jobId: data.id };
    } catch (err) {
      return {
        ok: false,
        reason: err instanceof Error ? err.message : String(err),
      };
    }
  };

  const handleImport = async (rawText: string, mode: "csv" | "json") => {
    if (!rawText.trim()) {
      toast.error("Paste content before importing.");
      return;
    }
    const hash = contentHash(rawText);
    let rowsTotal = 0;
    const mapped: Partial<Job>[] = [];
    let skipped = 0;
    let staged = 0;

    try {
      if (mode === "csv") {
        const rows = parseCsv(rawText);
        rowsTotal = rows.length;
        const batch = createBatch({
          sourceSystem: "jobber",
          contentHash: hash,
          rowsTotal,
          rowsMapped: 0,
          rowsSkippedDuplicate: 0,
        });
        for (const row of rows) {
          const partial = mapJobberCsvRow(row, { importBatchId: batch.id });
          const ext = partial.sourceMeta!.externalProjectId;
          const key = jobKey("jobber", ext);
          if (hasImportedKey(key) || existingImportedKeys.has(key)) { skipped++; continue; }
          markImportedKey(key);
          mapped.push(partial as Partial<Job>);
        }
      } else {
        const parsed = JSON.parse(rawText);
        const projects: MigrationJsonProject[] = Array.isArray(parsed) ? parsed : [parsed];
        rowsTotal = projects.length;
        const batch = createBatch({
          sourceSystem: "jobber",
          contentHash: hash,
          rowsTotal,
          rowsMapped: 0,
          rowsSkippedDuplicate: 0,
        });
        for (const proj of projects) {
          const partial = mapJobberJsonProject(proj, { importBatchId: batch.id });
          const ext = partial.sourceMeta!.externalProjectId;
          const key = jobKey("jobber", ext);
          if (hasImportedKey(key) || existingImportedKeys.has(key)) { skipped++; continue; }
          markImportedKey(key);
          mapped.push(partial as Partial<Job>);
        }
      }
    } catch (err) {
      toast.error(`Import failed: ${(err as Error).message}`);
      return;
    }

    if (mapped.length === 0) {
      toast.warning(`${rowsTotal} rows seen — all duplicates (${skipped} skipped).`);
      return;
    }

    try {
      const orgId = await getCurrentOrganizationId();
      if (!orgId || orgId === "pending-setup") {
        toast.error("Missing organization context for import staging.");
        return;
      }

      const { data: authData } = await supabase.auth.getUser();
      const authUserId = authData.user?.id ?? null;
      let profileId: string | null = null;
      if (authUserId) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("id")
          .eq("auth_user_id", authUserId)
          .maybeSingle();
        profileId = (profile as { id?: string } | null)?.id ?? null;
      }

      const { data: batchData, error: batchErr } = await supabase
        .from("job_import_batches")
        .insert({
          organization_id: orgId,
          source_system: "jobber",
          import_mode: mode,
          content_hash: hash,
          rows_total: rowsTotal,
          rows_staged: mapped.length,
          rows_skipped_duplicate: skipped,
          created_by: profileId,
        })
        .select("id")
        .single();
      if (batchErr || !batchData?.id) {
        toast.error("Failed to create import batch", { description: batchErr?.message ?? "Unknown error" });
        return;
      }

      const batchId = (batchData as { id: string }).id;
      const rowPayload = mapped.map((partial) => ({
        batch_id: batchId,
        organization_id: orgId,
        source_system: partial.sourceMeta?.sourceSystem ?? "jobber",
        external_project_id: partial.sourceMeta?.externalProjectId ?? partial.id ?? "unknown",
        customer_name: partial.customerName ?? null,
        payload: partial,
        status: "staged",
      }));
      const { error: rowsErr } = await supabase
        .from("job_import_rows")
        .insert(rowPayload);
      if (rowsErr) {
        toast.error("Failed to stage import rows", { description: rowsErr.message });
        return;
      }

      staged = rowPayload.length;
      try {
        await loadStagedImports();
      } catch {
        console.warn("Staged successfully but queue refresh failed.");
      }
    } catch (err) {
      toast.error("Failed to stage imports", {
        description: err instanceof Error ? err.message : String(err),
      });
      return;
    }

    if (staged > 0) {
      toast.success(`Staged ${staged} job(s) for review — ${skipped} duplicates skipped. No DB insert until approval.`);
    } else {
      toast.error(`No jobs staged. ${skipped} duplicates skipped.`);
    }
    if (mode === "csv") setCsvText("");
    else setJsonText("");
  };

  const downloadExcelTemplate = () => {
    const headers = [
      "id",
      "title",
      "description",
      "scope_summary",
      "notes",
      "signature_date",
      "total",
      "start_at",
      "end_at",
      "project_type",
      "project_subtype",
      "client_name",
      "client_phone",
      "client_email",
      "property_street",
      "property_city",
      "property_state",
      "property_zip",
      "custom_fields_install_type",
      "project_contractor",
      "line_items_json",
      "attachments_json",
      "job_metadata_materials_json",
      "job_metadata_photos_json",
      "job_metadata_trades_json",
      "scope_json",
      "schedule_start_date",
      "schedule_end_date",
    ];
    const exampleRow = [
      "QUOTE-1001",
      "Master Shower Remodel",
      "Full master bathroom shower remodel with glass enclosure and new fixtures.",
      "Remove existing tub, install new shower pan, glass walls, ceiling panel, and fixtures.",
      "Customer prefers brushed nickel finish. Access from hallway.",
      "2026-06-01",
      "8500",
      "2026-07-15",
      "2026-07-22",
      "Shower Remodel",
      "Full Bathroom",
      "Mark Bless",
      "8135550101",
      "mark@example.com",
      "123 Main St",
      "Tampa",
      "FL",
      "33602",
      "Shower Remodel",
      "John Smith",
      JSON.stringify([{ name: "Glass Enclosure 60x60", sku: "GLASS-001", quantity: 1 }, { name: "Grab Bar - 12\"", sku: "GB-12", quantity: "1"}]),
      JSON.stringify([{ url: "https://example.com/photo1.jpg" }, { url: "https://example.com/photo2.jpg"}]),
      JSON.stringify(["Tile", "Grout", "Sealant"]),
      JSON.stringify(["https://example.com/photo1.jpg"]),
      JSON.stringify({ demo: true, plumbing: true, electrical: false, permit: true }),
      JSON.stringify({
        shower_system: { type: "Custom Shower", pan: { size: "60x60", color: "White", drain: "Center" } },
        installation: { removal: { bathroom: 1, description: "Remove existing tub" } },
      }),
      JSON.stringify({ start_date: "2026-07-15", end_date: "2026-07-22", duration_days: 7 }),
    ];

    const ws = XLSX.utils.aoa_to_sheet([headers, exampleRow]);
    ws['!cols'] = headers.map(() => ({ wch: 24 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Migration");
    XLSX.writeFile(wb, "migration-template.xlsx");
    toast.success("Excel template downloaded.");
  };

  const handleExcelUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const name = file.name.toLowerCase();
    if (!name.endsWith(".xlsx") && !name.endsWith(".json")) {
      toast.error("Upload a .xlsx or .json file.");
      event.target.value = "";
      return;
    }

    try {
      setIsExcelBusy(true);
      const text = await file.text();
      let projects: MigrationJsonProject[];

      if (name.endsWith(".json")) {
        const parsed = JSON.parse(text);
        projects = Array.isArray(parsed) ? parsed : [parsed];
      } else {
        const data = new Uint8Array(await file.arrayBuffer());
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: "" });
        projects = rows.map((row) => {
          const get = (key: string) => {
            const val = row[key];
            if (val === undefined || val === null) return "";
            return String(val).trim();
          };
          const getNum = (key: string) => {
            const val = get(key);
            if (!val) return undefined;
            const n = Number(val);
            return Number.isFinite(n) ? n : undefined;
          };
          const getJson = (key: string): unknown => {
            const val = get(key);
            if (!val) return undefined;
            try {
              return JSON.parse(val);
            } catch {
              return undefined;
            }
          };

          const project: MigrationJsonProject = {} as MigrationJsonProject;
          const maybeSet = <K extends keyof MigrationJsonProject>(key: K, value: unknown) => {
            if (value !== undefined && value !== null && value !== "") (project as Record<K, unknown>)[key] = value;
          };

          maybeSet("id", get("id"));
          maybeSet("title", get("title"));
          maybeSet("description", get("description"));
          maybeSet("scope_summary", get("scope_summary"));
          maybeSet("notes", get("notes"));
          maybeSet("signature_date", get("signature_date"));
          maybeSet("total", getNum("total"));
          maybeSet("start_at", get("start_at"));
          maybeSet("end_at", get("end_at"));
          maybeSet("project_type", get("project_type"));
          maybeSet("project_subtype", get("project_subtype"));

          const clientName = get("client_name");
          const clientPhone = get("client_phone");
          const clientEmail = get("client_email");
          if (clientName || clientPhone || clientEmail) {
            maybeSet("client", {
              name: clientName,
              primary_phone: clientPhone,
              primary_email: clientEmail,
            });
          }

          const street = get("property_street");
          const city = get("property_city");
          const state = get("property_state");
          const zip = get("property_zip");
          if (street || city || state || zip) {
            maybeSet("property", { street, city, state, zip });
          }

          const installType = get("custom_fields_install_type");
          if (installType) {
            maybeSet("custom_fields", { install_type: installType });
          }

          const contractor = get("project_contractor");
          if (contractor) {
            maybeSet("project", { contractor });
          }

          const lineItems = getJson("line_items_json") as MigrationJsonProject["line_items"] | undefined;
          if (lineItems) maybeSet("line_items", lineItems);

          const attachments = getJson("attachments_json") as MigrationJsonProject["attachments"] | undefined;
          if (attachments) maybeSet("attachments", attachments);

          const jobMaterials = getJson("job_metadata_materials_json") as string[] | undefined;
          if (jobMaterials) maybeSet("job_metadata", { ...(project.job_metadata ?? {}), materials: jobMaterials });

          const jobPhotos = getJson("job_metadata_photos_json") as string[] | undefined;
          if (jobPhotos) maybeSet("job_metadata", { ...(project.job_metadata ?? {}), photos: jobPhotos });

          const jobTrades = getJson("job_metadata_trades_json") as Record<string, boolean> | undefined;
          if (jobTrades) maybeSet("job_metadata", { ...(project.job_metadata ?? {}), trades: jobTrades });

          const scope = getJson("scope_json") as MigrationJsonProject["scope"] | undefined;
          if (scope) maybeSet("scope", scope);

          const scheduleStart = get("schedule_start_date");
          const scheduleEnd = get("schedule_end_date");
          if (scheduleStart || scheduleEnd) {
            maybeSet("schedule", {
              start_date: scheduleStart,
              end_date: scheduleEnd,
            });
          }

          return project;
        });
      }

      if (projects.length === 0) {
        toast.warning("No valid project rows found in the file.");
        event.target.value = "";
        return;
      }

      setJsonText(JSON.stringify(projects, null, 2));
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error(`Failed to read file: ${message}`);
    } finally {
      setIsExcelBusy(false);
      event.target.value = "";
    }
  };

  const approve = async (job: Job): Promise<boolean> => {
    const flags = computeReviewFlags(job);
    if (hasBlockingFlag(flags)) {
      toast.error("Cannot approve — missing required fields.");
      return false;
    }
    const stagedRow = stagedImports.find(
      (row) =>
        row.partial.sourceMeta?.sourceSystem === job.sourceMeta?.sourceSystem &&
        row.partial.sourceMeta?.externalProjectId === job.sourceMeta?.externalProjectId,
    );

    const partial = stagedRow?.partial ?? job;
    const materials = normalizeImportedMaterialLines(partial.materialsList ?? []);
    const partialForApproval: Partial<Job> = {
      ...partial,
      materialsList: materials,
    };

    // Validate SKUs and identify unmapped ones for warehouse review.
    const { unmappedSkus } = await validateAndFlagUnmappedMaterials(materials);

    if (stagedRow) {
      // Keep legacy metadata for rollback, but seed the normalized warehouse review queue below.
      if (unmappedSkus.length > 0) {
        const updatedPartial = {
          ...partialForApproval,
          unmappedSkusForReview: unmappedSkus,
        };
        const result = await persistImportedJob(updatedPartial as Partial<Job>);
        if (!result.ok) {
          const failReason = (result as { ok: false; reason: string }).reason;
          await supabase
            .from("job_import_rows")
            .update({
              status: "failed",
              failure_reason: failReason,
              updated_at: new Date().toISOString(),
            })
            .eq("id", stagedRow.id);
          toast.error(`Approve failed: ${failReason}`);
          try { await loadStagedImports(); } catch { /* queue refresh failed */ }
          return false;
        }
        try {
          const reviewsOk = await WarehousePageService.createPendingSkuReviews({
            organizationId: await getCurrentOrganizationId(),
            jobId: result.jobId,
            jobLabel: partialForApproval.jobNumber || result.jobId,
            items: unmappedSkus,
            source: "migration_approval",
          });
          if (!reviewsOk) {
            toast.warning("Job approved, but some SKU review rows could not be queued.");
          }
        } catch (err) {
          console.warn("[MigrationReview] warehouse SKU review queue insert failed (non-fatal):", err);
          toast.warning("Job approved, but SKU review queue insert failed.");
        }
        await supabase
          .from("job_import_rows")
          .update({
            status: "approved",
            approved_job_id: result.jobId,
            approved_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            failure_reason: null,
          })
          .eq("id", stagedRow.id);
        toast.success(`Approved ${job.customerName || job.id} — inserted to DB. ${unmappedSkus.length} unmapped SKU(s) awaiting warehouse review.`);
      } else {
        const result = await persistImportedJob(partialForApproval);
        if (!result.ok) {
          const failReason = (result as { ok: false; reason: string }).reason;
          await supabase
            .from("job_import_rows")
            .update({
              status: "failed",
              failure_reason: failReason,
              updated_at: new Date().toISOString(),
            })
            .eq("id", stagedRow.id);
          toast.error(`Approve failed: ${failReason}`);
          try { await loadStagedImports(); } catch { /* queue refresh failed */ }
          return false;
        }
        await supabase
          .from("job_import_rows")
          .update({
            status: "approved",
            approved_job_id: result.jobId,
            approved_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            failure_reason: null,
          })
          .eq("id", stagedRow.id);
        toast.success(`Approved ${job.customerName || job.id} — inserted to DB and now visible to operations.`);
      }
      await refreshData();
      try { await loadStagedImports(); } catch { /* queue refresh failed */ }
      return true;
    }

    const patch: Partial<Job> = {
      sourceMeta: {
        ...(job.sourceMeta ?? {
          sourceSystem: "manual_import",
          readinessMode: "external_managed",
          externalProjectId: job.id,
          importedAt: new Date().toISOString(),
          importBatchId: "manual",
          migrationStatus: "needs_review",
          reviewFlags: [],
        }),
        migrationStatus: "approved",
        approvedAt: new Date().toISOString(),
        approvedBy: "operator",
        reviewFlags: flags,
      },
    };
    const { error } = await JobService.update(job.id, patch);
    if (error) {
      toast.error(`Approve failed: ${error instanceof Error ? error.message : "Unknown error"}`);
      return false;
    }
    await refreshData();
    toast.success(`Approved ${job.customerName || job.id} — now visible to operations.`);
    return true;
  };

  const reject = async (job: Job): Promise<boolean> => {
    const stagedRow = stagedImports.find(
      (row) =>
        row.partial.sourceMeta?.sourceSystem === job.sourceMeta?.sourceSystem &&
        row.partial.sourceMeta?.externalProjectId === job.sourceMeta?.externalProjectId,
    );
    if (stagedRow) {
      await supabase
        .from("job_import_rows")
        .update({
          status: "rejected",
          rejected_at: new Date().toISOString(),
          rejected_reason: "Rejected by operator",
          updated_at: new Date().toISOString(),
        })
        .eq("id", stagedRow.id);
      try { await loadStagedImports(); } catch { /* queue refresh failed */ }
      toast.message(`Rejected ${job.customerName || job.id}.`);
      return true;
    }

    const patch: Partial<Job> = {
      sourceMeta: {
        ...(job.sourceMeta ?? {
          sourceSystem: "manual_import",
          readinessMode: "external_managed",
          externalProjectId: job.id,
          importedAt: new Date().toISOString(),
          importBatchId: "manual",
          migrationStatus: "needs_review",
          reviewFlags: [],
        }),
        migrationStatus: "rejected",
        rejectedReason: "Rejected by operator",
      },
    };
    const { error } = await JobService.update(job.id, patch);
    if (error) {
      toast.error(`Reject failed: ${error instanceof Error ? error.message : "Unknown error"}`);
      return false;
    }
    await refreshData();
    toast.message(`Rejected ${job.customerName || job.id}.`);
    return true;
  };

  const renderProductSkuMatcher = ({
    matcherId,
    sku,
    disabled,
    onMatch,
  }: {
    matcherId: string;
    sku: string;
    disabled?: boolean;
    onMatch: (product: RegistryProduct) => void;
  }) => {
    const matchedProduct = catalogProductBySku.get(normalizeMaterialSku(sku));
    const isOpen = activeSkuMatcherId === matcherId;

    return (
      <div className="space-y-1">
        <Popover open={isOpen} onOpenChange={(open) => setActiveSkuMatcherId(open ? matcherId : null)}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 w-full min-w-0 justify-start px-2 text-xs"
              disabled={disabled}
            >
              <span className="truncate">
                {matchedProduct ? matchedProduct.sku : "Match Product Registry SKU"}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[360px] p-0" align="end">
            <Command className="overflow-visible">
              <CommandInput placeholder="Search Product Registry name or SKU..." />
              <CommandList className="max-h-[260px] overflow-y-scroll pointer-events-auto">
                <CommandEmpty>
                  {catalogProductsLoading ? "Loading Product Registry..." : "No Product Registry SKU found"}
                </CommandEmpty>
                <CommandGroup>
                  {catalogProducts.map((product) => (
                    <CommandItem
                      key={product.productId}
                      value={`${product.sku} ${product.productName} ${product.category} ${product.vendor}`}
                      onSelect={() => {
                        onMatch(product);
                        setActiveSkuMatcherId(null);
                      }}
                    >
                      <div className="min-w-0">
                        <div className="truncate text-xs font-medium">{product.productName}</div>
                        <div className="truncate text-[10px] text-muted-foreground">
                          {product.sku} · {product.category}{product.vendor ? ` · ${product.vendor}` : ""}
                        </div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <div className="flex min-w-0 items-center gap-1">
          {matchedProduct ? (
            <>
              <Badge variant="secondary" className="shrink-0 text-[10px]">Matched</Badge>
              <span className="truncate text-[10px] text-muted-foreground">{matchedProduct.productName}</span>
            </>
          ) : (
            <Badge variant="outline" className="border-warning/40 text-[10px] text-warning">Unmatched</Badge>
          )}
        </div>
      </div>
    );
  };

  const renderStagedEditor = (row: StagedImport) => {
    const partial = row.partial;
    const materials = partial.materialsList ?? [];
    const photos = partial.photos ?? [];
    const photoGroups = normalizePhotoGroups(partial);
    const groupedPhotoRefs = new Set(photoGroups.flatMap((group) => group.photoRefs));
    const ungroupedPhotos = photos.filter((photo) => !groupedPhotoRefs.has(photo));
    const isBusy = busyImportRowId === row.id;
    const contract = partial.contractDocument;
    const contractFileSize = formatFileSize(contract?.fileSize);
    const isGeneratingScope = generatingScopeRowId === row.id;
    const canGenerateScope = Boolean(contract?.filePath && isPdfContractDocument(contract));
    const cachedScopeDraft = isCachedScopeDraftForContract(partial.migrationImportDetail?.aiScopeDraft, contract)
      ? partial.migrationImportDetail?.aiScopeDraft
      : undefined;
    const groupedPhotoUploadsEnabled = groupedPhotoUploadsEnabledByRow[row.id] ?? hasPhotoGrouping(photoGroups);
    const currentAddress = partial.address ?? { street: "", city: "", state: "", zip: "" };
    const projectTypeValue = PROJECT_TYPES.includes(partial.projectType as ProjectType)
      ? (partial.projectType as ProjectType)
      : "Other";
    const setRowGroupedPhotoUploadsEnabled = (enabled: boolean) => {
      setGroupedPhotoUploadsEnabledByRow((prev) => ({ ...prev, [row.id]: enabled }));
    };

    const updateAddressField = (field: keyof Job["address"], value: string) => {
      updateLocalStagedImport(row.id, {
        address: {
          ...currentAddress,
          [field]: value,
        },
      });
    };

    const updateMaterials = (nextMaterials: MaterialLine[], immediate = false) => {
      const nextPartial = buildUpdatedStagedPartial(row.partial, { materialsList: nextMaterials });
      updateLocalStagedImport(row.id, { materialsList: nextMaterials });

      const existing = materialsDebounceByRow.current[row.id];
      if (existing) clearTimeout(existing);

      materialsDebounceByRow.current[row.id] = setTimeout(() => {
        void saveStagedImport(row.id, nextPartial, { silent: true });
        delete materialsDebounceByRow.current[row.id];
      }, immediate ? 0 : 800);
    };

    const addPhotoGroup = () => {
      const label = newPhotoGroupLabelByRow[row.id]?.trim();
      if (!label) {
        toast.warning("Enter a photo group name first.");
        return;
      }
      const nextGroups = [...photoGroups, createPhotoGroup(label)];
      updateLocalStagedImport(row.id, withPhotoGroups(partial, nextGroups));
      setNewPhotoGroupLabelByRow((prev) => ({ ...prev, [row.id]: "" }));
    };

    const updatePhotoGroupLabel = (groupId: string, label: string) => {
      const nextGroups = photoGroups.map((group) =>
        group.id === groupId ? { ...group, label } : group,
      );
      updateLocalStagedImport(row.id, withPhotoGroups(partial, nextGroups));
    };

    const removePhotoGroup = (groupId: string) => {
      const nextGroups = photoGroups.filter((group) => group.id !== groupId);
      updateLocalStagedImport(row.id, withPhotoGroups(partial, nextGroups));
    };

    const PHOTOS_PER_PAGE = 8;

    const openPhotoGallery = (title: string, photosToView: string[], index: number) => {
      if (photosToView.length === 0) return;
      setPhotoGallery({
        title,
        photos: photosToView,
        index: Math.max(0, Math.min(index, photosToView.length - 1)),
      });
    };

    const renderPhotoGrid = (
      title: string,
      gridPhotos: string[],
      groupId?: string,
      options?: { showRemoveGroup?: boolean; description?: string },
    ) => {
      const totalPhotoPages = Math.max(1, Math.ceil(gridPhotos.length / PHOTOS_PER_PAGE));
      const rawPhotoPage = photoPageByRow[groupId ?? row.id] ?? 0;
      const photoPage = Math.min(rawPhotoPage, Math.max(0, totalPhotoPages - 1));
      const photoStart = photoPage * PHOTOS_PER_PAGE;
      const visiblePhotos = gridPhotos.slice(photoStart, photoStart + PHOTOS_PER_PAGE);
      const showPhotoPagination = gridPhotos.length > PHOTOS_PER_PAGE;
      const pageKey = groupId ?? row.id;
      const setPage = (nextPage: number) => setPhotoPageByRow((prev) => ({ ...prev, [pageKey]: nextPage }));

      return (
        <div className="space-y-2 rounded-md border bg-background p-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-medium">{title}</div>
              <div className="text-xs text-muted-foreground">
                {options?.description ?? `${gridPhotos.length} photo${gridPhotos.length === 1 ? "" : "s"}.`}
              </div>
            </div>
            {options?.showRemoveGroup && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={isBusy}
                onClick={() => removePhotoGroup(groupId as string)}
              >
                Remove group
              </Button>
            )}
          </div>
          <Input
            className="max-w-xs"
            type="file"
            accept="image/*"
            multiple
            disabled={isBusy}
            onChange={(event) => {
              void uploadImportPhotos(row, event.target.files, groupId);
              event.target.value = "";
            }}
          />
          <PhotoUploadStatusRow
            rowId={row.id}
            groupId={groupId}
            statusByKey={uploadStatusByKey}
            batchByScope={uploadBatchByScope}
          />
          {gridPhotos.length > 0 && (
            <>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                {visiblePhotos.map((photo, visibleIndex) => {
                  const globalIndex = photoStart + visibleIndex;
                  return (
                    <div key={`${photo}-${globalIndex}-${groupId ?? "legacy"}`} className="rounded-md border bg-background p-2 space-y-2">
                      <ImportPhotoPreview photo={photo} alt={`${title} photo ${globalIndex + 1}`} />
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full gap-1.5"
                        onClick={() => openPhotoGallery(title, gridPhotos, globalIndex)}
                      >
                        <Eye className="h-3.5 w-3.5" /> View Photo
                      </Button>
                      <div className="grid grid-cols-2 gap-2">
                        <Button asChild size="sm" variant="outline" className="cursor-pointer">
                          <label>
                            Replace
                            <input
                              className="hidden"
                              type="file"
                              accept="image/*"
                              disabled={isBusy}
                              onChange={(event) => {
                                void replaceImportPhoto(row, photo, event.target.files, groupId);
                                event.target.value = "";
                              }}
                            />
                          </label>
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={isBusy}
                          onClick={() => void removeImportPhoto(row, photo, groupId)}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {showPhotoPagination && (
                <div className="flex items-center justify-between gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={photoPage === 0}
                    onClick={() => setPage(Math.max(0, photoPage - 1))}
                  >
                    Previous
                  </Button>
                  <div className="text-xs text-muted-foreground">
                    Page {photoPage + 1} of {totalPhotoPages}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={photoPage >= totalPhotoPages - 1}
                    onClick={() => setPage(Math.min(totalPhotoPages - 1, photoPage + 1))}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      );
    };

    return (
      <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div>
            <div className="text-sm font-medium">Review edits</div>
            <div className="text-xs text-muted-foreground">
              Changes are saved to the staged import payload and used when this job is approved.
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={isBusy}
            onClick={() => saveStagedImport(row.id)}
          >
            {isBusy ? "Saving..." : "Save edits"}
          </Button>
        </div>

        <div className="rounded-md border bg-muted/20 p-3 space-y-3">
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Customer / Project</div>
            <div className="text-xs text-muted-foreground">
              Correct imported identity details before approving the staged job.
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <label className="space-y-1">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Name</span>
              <Input
                value={partial.customerName ?? ""}
                disabled={isBusy}
                onChange={(event) => updateLocalStagedImport(row.id, { customerName: event.target.value })}
                placeholder="Customer name"
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Phone</span>
              <Input
                type="tel"
                inputMode="numeric"
                value={partial.phone ?? ""}
                disabled={isBusy}
                onChange={(event) => updateLocalStagedImport(row.id, { phone: formatPhoneNumber(event.target.value) })}
                placeholder="Customer phone"
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Email</span>
              <Input
                type="email"
                value={partial.email ?? ""}
                disabled={isBusy}
                onChange={(event) => updateLocalStagedImport(row.id, { email: event.target.value })}
                placeholder="Customer email"
              />
            </label>
          </div>
          <div className="grid gap-3 md:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_120px_140px]">
            <label className="space-y-1">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Street Address</span>
              <Input
                value={currentAddress.street}
                disabled={isBusy}
                onChange={(event) => updateAddressField("street", event.target.value)}
                placeholder="Street address"
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">City</span>
              <Input
                value={currentAddress.city}
                disabled={isBusy}
                onChange={(event) => updateAddressField("city", event.target.value)}
                placeholder="City"
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">State</span>
              <Input
                value={currentAddress.state}
                disabled={isBusy}
                onChange={(event) => updateAddressField("state", event.target.value)}
                placeholder="State"
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">ZIP</span>
              <Input
                value={currentAddress.zip}
                disabled={isBusy}
                onChange={(event) => updateAddressField("zip", event.target.value)}
                placeholder="ZIP"
              />
            </label>
          </div>
          <label className="block space-y-1">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Project Type</span>
            <select
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={projectTypeValue}
              disabled={isBusy}
              onChange={(event) => updateLocalStagedImport(row.id, { projectType: event.target.value as ProjectType })}
            >
              {PROJECT_TYPES.map((projectType) => (
                <option key={projectType} value={projectType}>
                  {projectType}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* ── Bypass selector ─────────────────────────────────────── */}
        <div className="rounded-md border bg-muted/20 p-3 space-y-2">
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Stage Bypass</div>
            <div className="text-xs text-muted-foreground">
              Select which operational stages this migrated job bypasses. Bypassed stages will not block job progression.
            </div>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {MIGRATION_BYPASS_OPTIONS.map(({ key, label }) => {
              const bypassedStages = partial.sourceMeta?.bypassedStages ?? DEFAULT_MIGRATION_BYPASSES;
              const checked = bypassedStages.includes(key);
              return (
                <label key={key} className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={isBusy}
                    onChange={(e) => {
                      const current = partial.sourceMeta?.bypassedStages ?? DEFAULT_MIGRATION_BYPASSES;
                      const next = e.target.checked
                        ? Array.from(new Set([...current, key]))
                        : current.filter((s) => s !== key);
                      const nextSourceMeta: NonNullable<Job["sourceMeta"]> = {
                        ...(partial.sourceMeta ?? {
                          sourceSystem: "jobber",
                          readinessMode: "external_managed",
                          externalProjectId: partial.id ?? row.id,
                          importedAt: row.importedAt,
                          importBatchId: row.batchId,
                          migrationStatus: "needs_review",
                          reviewFlags: [],
                        }),
                        bypassedStages: next,
                      };
                      updateLocalStagedImport(row.id, {
                        sourceMeta: nextSourceMeta,
                      });
                      void saveStagedImport(
                        row.id,
                        buildUpdatedStagedPartial(row.partial, {
                          sourceMeta: nextSourceMeta,
                        }),
                        { silent: true },
                      );
                    }}
                  />
                  {label}
                </label>
              );
            })}
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Scope</div>
            <Textarea
              rows={4}
              value={partial.scopeSummary ?? ""}
              onChange={(event) => updateLocalStagedImport(row.id, { scopeSummary: event.target.value })}
              placeholder="Install scope, room, options, constraints..."
            />
          </div>
          <div className="space-y-1">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Description / notes
            </div>
            <Textarea
              rows={4}
              value={partial.notes ?? ""}
              onChange={(event) => updateLocalStagedImport(row.id, { notes: event.target.value })}
              placeholder="Customer notes, field context, import description..."
            />
          </div>
        </div>

        <div className="rounded-md border bg-muted/20 p-3 space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Contract</div>
              <div className="text-xs text-muted-foreground">
                Upload the signed/proposal contract now; it will attach to the job when approved.
              </div>
            </div>
            <Button asChild size="sm" variant="outline" className="cursor-pointer">
              <label>
                {contract?.filePath ? "Replace contract" : "Upload contract"}
                <input
                  className="hidden"
                  type="file"
                  accept="application/pdf,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  disabled={isBusy}
                  onChange={(event) => {
                    void uploadImportContract(row, event.target.files);
                    event.target.value = "";
                  }}
                />
              </label>
            </Button>
          </div>

          {contract ? (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border bg-background p-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">
                    {contract.fileName ?? "Imported contract record"}
                  </span>
                  <Badge variant={contract.status === "Signed" ? "default" : "secondary"}>
                    {contract.status}
                  </Badge>
                  {cachedScopeDraft && (
                    <Badge variant="secondary">AI draft cached</Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {contract.filePath
                    ? [
                        contract.mimeType || "contract file",
                        contractFileSize,
                        contract.uploadedAt ? `uploaded ${new Date(contract.uploadedAt).toLocaleString()}` : "",
                      ].filter(Boolean).join(" · ")
                    : "Contract metadata exists, but no file has been uploaded yet."}
                </div>
                {contract.homeownerSignedAt && (
                  <div className="text-xs text-muted-foreground">
                    Signed {new Date(contract.homeownerSignedAt).toLocaleString()}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={isBusy || isGeneratingScope || !canGenerateScope}
                  onClick={() => void generateInstallerScope(row)}
                >
                  {isGeneratingScope ? "Generating..." : cachedScopeDraft ? "View AI draft" : "Generate scope"}
                </Button>
                {cachedScopeDraft && canGenerateScope && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={isBusy || isGeneratingScope}
                    onClick={() => void generateInstallerScope(row, { force: true })}
                  >
                    Regenerate
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={isBusy || !contract.filePath}
                  onClick={() => void viewImportContract(contract)}
                >
                  View
                </Button>
                {contract.filePath && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={isBusy}
                    onClick={() => void removeImportContract(row)}
                  >
                    Remove upload
                  </Button>
                )}
              </div>
              {contract.filePath && !isPdfContractDocument(contract) && (
                <div className="w-full text-xs text-amber-600">
                  AI scope generation supports PDF contracts only. Upload a PDF to generate installer scope.
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
              No contract uploaded or detected yet.
            </div>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Job photos</div>
              <div className="text-xs text-muted-foreground">
                {photos.length} photo{photos.length === 1 ? "" : "s"} staged for this job.
              </div>
            </div>
            <label className="flex items-center gap-2 whitespace-nowrap text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={groupedPhotoUploadsEnabled}
                disabled={isBusy}
                onChange={(event) => setRowGroupedPhotoUploadsEnabled(event.target.checked)}
              />
              Separate by project
            </label>
          </div>

          {groupedPhotoUploadsEnabled ? (
            <div className="space-y-3">
              <div className="flex gap-2">
                <Input
                  value={newPhotoGroupLabelByRow[row.id] ?? ""}
                  onChange={(event) => setNewPhotoGroupLabelByRow((prev) => ({ ...prev, [row.id]: event.target.value }))}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") addPhotoGroup();
                  }}
                  placeholder="Add photo group, e.g. Guest Bathroom"
                  disabled={isBusy}
                />
                <Button type="button" size="sm" variant="outline" disabled={isBusy} onClick={addPhotoGroup}>
                  Add group
                </Button>
              </div>

              {photoGroups.length > 0 ? (
                <div className="space-y-3">
                  {photoGroups.map((group) => (
                    <div key={group.id}>
                      <Input
                        value={group.label}
                        onChange={(event) => updatePhotoGroupLabel(group.id, event.target.value)}
                        disabled={isBusy}
                        className="mb-2"
                      />
                      {renderPhotoGrid(group.label, group.photoRefs, group.id, { showRemoveGroup: true })}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                  Add groups such as `Guest Bathroom` and `Room Bathroom`, then upload photos under each group.
                </div>
              )}

              {ungroupedPhotos.length > 0 && renderPhotoGrid("Ungrouped photos", ungroupedPhotos, undefined, {
                description: `${ungroupedPhotos.length} existing photo${ungroupedPhotos.length === 1 ? "" : "s"} not assigned to a project group.`,
              })}
            </div>
          ) : renderPhotoGrid("All job photos", photos, undefined, {
            description: "Legacy view. Enable the temporary toggle to separate uploads by project.",
          })}
        </div>

        {/* ── Migration Details (read-only parsed spec) ────────────── */}
        {partial.migrationImportDetail && (
          <div className="rounded-md border bg-muted/10 p-3 space-y-3">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Parsed Migration Details
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {partial.migrationImportDetail.contractor && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground">Contractor</div>
                  <div className="text-sm">{partial.migrationImportDetail.contractor}</div>
                </div>
              )}
              {partial.migrationImportDetail.projectSubtype && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground">Project Subtype</div>
                  <div className="text-sm">{partial.migrationImportDetail.projectSubtype}</div>
                </div>
              )}
              {partial.migrationImportDetail.showerSystem?.type && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground">Shower System</div>
                  <div className="text-sm">{partial.migrationImportDetail.showerSystem.type}</div>
                  {partial.migrationImportDetail.showerSystem.pan && (
                    <div className="text-xs text-muted-foreground">
                      Pan: {partial.migrationImportDetail.showerSystem.pan.size} · {partial.migrationImportDetail.showerSystem.pan.color} · Drain: {partial.migrationImportDetail.showerSystem.pan.drain}
                      {partial.migrationImportDetail.showerSystem.pan.drain_note ? ` (${partial.migrationImportDetail.showerSystem.pan.drain_note})` : ""}
                    </div>
                  )}
                  {partial.migrationImportDetail.showerSystem.walls && (
                    <div className="text-xs text-muted-foreground">
                      Walls: {partial.migrationImportDetail.showerSystem.walls.material} · {partial.migrationImportDetail.showerSystem.walls.color} · {partial.migrationImportDetail.showerSystem.walls.height}
                      {partial.migrationImportDetail.showerSystem.walls.finish ? ` · ${partial.migrationImportDetail.showerSystem.walls.finish}` : ""}
                    </div>
                  )}
                  {partial.migrationImportDetail.showerSystem.accessories && partial.migrationImportDetail.showerSystem.accessories.length > 0 && (
                    <div className="text-xs text-muted-foreground">
                      Accessories: {partial.migrationImportDetail.showerSystem.accessories.map(a => `${a.type}${a.size ? ` ${a.size}"` : ""}${a.quantity ? ` x${a.quantity}` : ""}`).join(", ")}
                    </div>
                  )}
                </div>
              )}
              {partial.migrationImportDetail.installation && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground">Installation</div>
                  {partial.migrationImportDetail.installation.removal && (
                    <div className="text-xs text-muted-foreground">
                      Removal: Bath {partial.migrationImportDetail.installation.removal.bathroom} — {partial.migrationImportDetail.installation.removal.description}
                    </div>
                  )}
                  {partial.migrationImportDetail.installation.thresholds && (
                    <div className="text-xs text-muted-foreground">
                      Thresholds: Bath {partial.migrationImportDetail.installation.thresholds.bathroom} — {partial.migrationImportDetail.installation.thresholds.description}
                    </div>
                  )}
                  {partial.migrationImportDetail.installation.new_installation && (
                    <div className="text-xs text-muted-foreground">
                      New install: Bath {partial.migrationImportDetail.installation.new_installation.bathroom} — {partial.migrationImportDetail.installation.new_installation.description}
                    </div>
                  )}
                </div>
              )}
              {partial.migrationImportDetail.plumbing && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground">Plumbing</div>
                  {partial.migrationImportDetail.plumbing.items && partial.migrationImportDetail.plumbing.items.length > 0 && (
                    <div className="text-xs text-muted-foreground">
                      Items: {partial.migrationImportDetail.plumbing.items.join(", ")}
                    </div>
                  )}
                  {partial.migrationImportDetail.plumbing.multiplier && (
                    <div className="text-xs text-muted-foreground">Multiplier: {partial.migrationImportDetail.plumbing.multiplier}x</div>
                  )}
                  {partial.migrationImportDetail.plumbing.notes && (
                    <div className="text-xs text-muted-foreground">{partial.migrationImportDetail.plumbing.notes}</div>
                  )}
                </div>
              )}
              {partial.migrationImportDetail.doors && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground">Doors</div>
                  {partial.migrationImportDetail.doors.bathroom_1 && (
                    <div className="text-xs text-muted-foreground">
                      Bath 1: {partial.migrationImportDetail.doors.bathroom_1.type} · {partial.migrationImportDetail.doors.bathroom_1.glass} · {partial.migrationImportDetail.doors.bathroom_1.finish} · {partial.migrationImportDetail.doors.bathroom_1.style}
                    </div>
                  )}
                  {partial.migrationImportDetail.doors.bathroom_2 && (
                    <div className="text-xs text-muted-foreground">
                      Bath 2: {partial.migrationImportDetail.doors.bathroom_2.type} · {partial.migrationImportDetail.doors.bathroom_2.glass} · {partial.migrationImportDetail.doors.bathroom_2.finish} · {partial.migrationImportDetail.doors.bathroom_2.style}
                    </div>
                  )}
                  {partial.migrationImportDetail.doors.window_kit && (
                    <div className="text-xs text-muted-foreground">
                      Window Kit: {partial.migrationImportDetail.doors.window_kit.type} · {partial.migrationImportDetail.doors.window_kit.color} (Bath {partial.migrationImportDetail.doors.window_kit.bathroom})
                    </div>
                  )}
                  {partial.migrationImportDetail.doors.type && !partial.migrationImportDetail.doors.bathroom_1 && !partial.migrationImportDetail.doors.bathroom_2 && (
                    <div className="text-xs text-muted-foreground">
                      {partial.migrationImportDetail.doors.type} · {partial.migrationImportDetail.doors.glass} · {partial.migrationImportDetail.doors.finish} · {partial.migrationImportDetail.doors.style}
                    </div>
                  )}
                </div>
              )}
              {partial.migrationImportDetail.fixtures && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground">Fixtures</div>
                  <div className="text-sm">{partial.migrationImportDetail.fixtures.type}</div>
                  {partial.migrationImportDetail.fixtures.finish && (
                    <div className="text-xs text-muted-foreground">Finish: {partial.migrationImportDetail.fixtures.finish}</div>
                  )}
                  {partial.migrationImportDetail.fixtures.note && (
                    <div className="text-xs text-muted-foreground">{partial.migrationImportDetail.fixtures.note}</div>
                  )}
                </div>
              )}
              {partial.migrationImportDetail.vanity && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground">Vanity</div>
                  <div className="text-sm">{partial.migrationImportDetail.vanity.type} · {partial.migrationImportDetail.vanity.size}</div>
                  <div className="text-xs text-muted-foreground">Style: {partial.migrationImportDetail.vanity.style}</div>
                  {partial.migrationImportDetail.vanity.countertop && (
                    <div className="text-xs text-muted-foreground">
                      Countertop: {partial.migrationImportDetail.vanity.countertop.material} · {partial.migrationImportDetail.vanity.countertop.style}
                    </div>
                  )}
                  {partial.migrationImportDetail.vanity.faucet && (
                    <div className="text-xs text-muted-foreground">
                      Faucet: {partial.migrationImportDetail.vanity.faucet.type} · {partial.migrationImportDetail.vanity.faucet.finish}
                    </div>
                  )}
                </div>
              )}
              {partial.migrationImportDetail.electrical && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground">Electrical & Ceiling</div>
                  {partial.migrationImportDetail.electrical.tasks && partial.migrationImportDetail.electrical.tasks.length > 0 && (
                    <div className="text-xs text-muted-foreground">
                      {partial.migrationImportDetail.electrical.tasks.join(", ")}
                    </div>
                  )}
                  {partial.migrationImportDetail.electrical.note && (
                    <div className="text-xs text-muted-foreground">{partial.migrationImportDetail.electrical.note}</div>
                  )}
                </div>
              )}
              {partial.migrationImportDetail.toilet && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground">Toilet</div>
                  <div className="text-sm">{partial.migrationImportDetail.toilet.type}</div>
                  <div className="text-xs text-muted-foreground">
                    {partial.migrationImportDetail.toilet.height} · Rough-in: {partial.migrationImportDetail.toilet.rough_in} · Finish: {partial.migrationImportDetail.toilet.finish}
                  </div>
                </div>
              )}
              {partial.migrationImportDetail.flooring && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground">Flooring</div>
                  <div className="text-sm">{partial.migrationImportDetail.flooring.type} · {partial.migrationImportDetail.flooring.color}</div>
                  <div className="text-xs text-muted-foreground">
                    {partial.migrationImportDetail.flooring.sqft} sq ft · {partial.migrationImportDetail.flooring.application}
                  </div>
                </div>
              )}
            </div>

            {partial.migrationImportDetail.jobMetadata && (
              <div className="grid gap-3 md:grid-cols-2">
                {partial.migrationImportDetail.jobMetadata.line_items && partial.migrationImportDetail.jobMetadata.line_items.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground">Line Items</div>
                    <ul className="text-xs space-y-0.5 list-disc list-inside">
                      {partial.migrationImportDetail.jobMetadata.line_items.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {partial.migrationImportDetail.jobMetadata.deliverables && partial.migrationImportDetail.jobMetadata.deliverables.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground">Deliverables</div>
                    <ul className="text-xs space-y-0.5 list-disc list-inside">
                      {partial.migrationImportDetail.jobMetadata.deliverables.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {partial.migrationImportDetail.jobMetadata.trades && (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground">Trades Required</div>
                    <div className="text-xs space-x-2">
                      {partial.migrationImportDetail.jobMetadata.trades.demo !== undefined && <span>Demo: {partial.migrationImportDetail.jobMetadata.trades.demo ? "Yes" : "No"}</span>}
                      {partial.migrationImportDetail.jobMetadata.trades?.plumbing !== undefined && <span>Plumbing: {partial.migrationImportDetail.jobMetadata.trades.plumbing ? "Yes" : "No"}</span>}
                      {partial.migrationImportDetail.jobMetadata.trades?.electrical !== undefined && <span>Electrical: {partial.migrationImportDetail.jobMetadata.trades.electrical ? "Yes" : "No"}</span>}
                      {partial.migrationImportDetail.jobMetadata.trades?.permit !== undefined && <span>Permit: {partial.migrationImportDetail.jobMetadata.trades.permit ? "Yes" : "No"}</span>}
                    </div>
                  </div>
                )}
                {partial.migrationImportDetail.jobMetadata.expectations && (
                  <div>
                    <div className="text-xs font-medium text-muted-foreground">Expectations</div>
                    <div className="text-xs space-y-0.5">
                      {partial.migrationImportDetail.jobMetadata.expectations.arrival && <div>Arrival: {partial.migrationImportDetail.jobMetadata.expectations.arrival}</div>}
                      {partial.migrationImportDetail.jobMetadata.expectations.customer_comms && <div>Comms: {partial.migrationImportDetail.jobMetadata.expectations.customer_comms}</div>}
                      {partial.migrationImportDetail.jobMetadata.expectations.qa && <div>QA: {partial.migrationImportDetail.jobMetadata.expectations.qa}</div>}
                      {partial.migrationImportDetail.jobMetadata.expectations.completion && <div>Completion: {partial.migrationImportDetail.jobMetadata.expectations.completion}</div>}
                      {partial.migrationImportDetail.jobMetadata.expectations.callback && <div>Callback: {partial.migrationImportDetail.jobMetadata.expectations.callback}</div>}
                    </div>
                  </div>
                )}
              </div>
            )}

            {partial.migrationImportDetail.scopeWorkItems && partial.migrationImportDetail.scopeWorkItems.length > 0 && (
              <div>
                <div className="text-xs font-medium text-muted-foreground">Scope Work Items</div>
                <ul className="text-xs space-y-0.5 list-disc list-inside">
                  {partial.migrationImportDetail.scopeWorkItems.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Materials</div>
              <div className="text-xs text-muted-foreground">
                Add imported material lines before approving the job.
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => updateMaterials([...materials, emptyMaterialLine()], true)}
            >
              Add material
            </Button>
          </div>
          {(() => {
            const unmatched = materials.filter(
              (m) => m.sku && !catalogProductBySku.get(normalizeMaterialSku(m.sku)),
            );
            return canRegisterProduct && unmatched.length > 0 ? (
              <div className="flex items-center justify-between rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning">
                <span>
                  {unmatched.length} material{unmatched.length === 1 ? '' : 's'} not in Product Registry
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-2 text-xs text-warning hover:text-warning"
                  onClick={() => {
                    setQuickRegisterPrefill({ name: unmatched[0].itemName, sku: unmatched[0].sku });
                    setQuickRegisterOpen(true);
                  }}
                >
                  Register first
                </Button>
              </div>
            ) : null;
          })()}
          {materials.length === 0 ? (
            <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
              No materials staged yet.
            </div>
          ) : (
            <div className="space-y-2">
              {materials.map((material, index) => (
                <div key={index} className="grid gap-2 rounded-md border bg-background p-2 md:grid-cols-[1fr_1fr_minmax(130px,auto)_90px_110px_auto]">
                  <Input
                    value={material.itemName}
                    onChange={(event) => {
                      const next = [...materials];
                      next[index] = { ...material, itemName: event.target.value };
                      updateMaterials(next);
                    }}
                    placeholder="Material description"
                  />
                  <Input
                    value={material.sku}
                    onChange={(event) => {
                      const next = [...materials];
                      next[index] = { ...material, sku: normalizeMaterialSku(event.target.value) };
                      updateMaterials(next);
                    }}
                    placeholder="SKU / external item"
                  />
                  {renderProductSkuMatcher({
                    matcherId: `staged-material:${row.id}:${index}`,
                    sku: material.sku,
                    disabled: isBusy,
                    onMatch: (product) => {
                      const next = [...materials];
                      next[index] = {
                        ...material,
                        sku: normalizeMaterialSku(product.sku),
                        itemName: product.productName,
                        unitCost: product.cost,
                        unitPrice: product.price,
                      };
                      updateMaterials(next, true);
                    },
                  })}
                  <Input
                    type="number"
                    min={0}
                    value={material.quantityRequired}
                    onChange={(event) => {
                      const next = [...materials];
                      next[index] = {
                        ...material,
                        quantityRequired: Number(event.target.value || 0),
                      };
                      updateMaterials(next);
                    }}
                    placeholder="Qty"
                  />
                  <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center text-xs text-muted-foreground">$</span>
                    <Input
                      type="number"
                      min={0}
                      step={0.01}
                      value={material.unitCost ?? ""}
                      onChange={(event) => {
                        const next = [...materials];
                        const val = event.target.value === "" ? undefined : Number(event.target.value);
                        next[index] = { ...material, unitCost: val, unitPrice: val };
                        updateMaterials(next);
                      }}
                      placeholder="Unit cost"
                      className="pl-5"
                    />
                  </div>
                  {canRegisterProduct && material.sku && !catalogProductBySku.get(normalizeMaterialSku(material.sku)) && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-warning hover:text-warning px-2"
                      onClick={() => {
                        setQuickRegisterPrefill({ name: material.itemName, sku: material.sku });
                        setQuickRegisterOpen(true);
                      }}
                    >
                      + Register
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => updateMaterials(materials.filter((_, materialIndex) => materialIndex !== index), true)}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const selectedJob = pending.find((job) => getJobQueueKey(job) === selectedJobKey) ?? null;
  const selectedStagedRow = selectedJob ? findStagedRowForJob(selectedJob) : undefined;
  const selectedFlags = selectedJob ? computeReviewFlags(selectedJob) : [];
  const selectedBlocked = hasBlockingFlag(selectedFlags);
  const activeScopeDraft = scopeDraftDialog?.generation ?? null;
  const activeScopeColorSelections = activeScopeDraft?.colorSelections ?? [];
  const activeScopePanelBreakdown = activeScopeDraft?.panelBreakdown ?? [];
  const activeScopeWarnings = activeScopeDraft?.warnings ?? [];
  const activeScopeMissingFields = activeScopeDraft?.missingFields ?? [];
  const activeScopeExtractedMaterials = activeScopeDraft?.extractedMaterials ?? [];
  const activeScopeSelectedMaterialIndexSet = new Set(scopeDraftDialog?.selectedMaterialHintIndexes ?? []);
  const activeScopeSelectedMaterialCount = activeScopeExtractedMaterials.filter((_, index) =>
    activeScopeSelectedMaterialIndexSet.has(index),
  ).length;

  const setAllScopeMaterialHintsSelected = (selected: boolean) => {
    setScopeDraftDialog((current) =>
      current
        ? {
            ...current,
            selectedMaterialHintIndexes: selected
              ? current.generation.extractedMaterials.map((_, index) => index)
              : [],
          }
        : current,
    );
  };

  const toggleScopeMaterialHint = (index: number, selected: boolean) => {
    setScopeDraftDialog((current) => {
      if (!current) return current;
      const next = new Set(current.selectedMaterialHintIndexes);
      if (selected) {
        next.add(index);
      } else {
        next.delete(index);
      }
      return {
        ...current,
        selectedMaterialHintIndexes: Array.from(next).sort((a, b) => a - b),
      };
    });
  };

  const updateScopeMaterialHint = (index: number, patch: Partial<MigrationAiScopeMaterialDraft>) => {
    setScopeDraftDialog((current) => {
      if (!current) return current;
      const extractedMaterials = current.generation.extractedMaterials.map((material, materialIndex) =>
        materialIndex === index
          ? normalizeAiMaterialHint({ ...material, ...patch })
          : material,
      );
      return {
        ...current,
        generation: {
          ...current.generation,
          extractedMaterials,
        },
      };
    });
  };

  useEffect(() => {
    if (selectedJobKey && !selectedJob) {
      setSelectedJobKey(null);
    }
  }, [selectedJob, selectedJobKey]);

  const approveAndClose = async (job: Job) => {
    const approved = await approve(job);
    if (approved) {
      // Show PushNextStepPanel so operator can chain to next action without
      // bouncing back to the queue. Build an optimistic post-approval snapshot:
      // migrationStatus → "approved" so bypass flags resolve correctly.
      const bypassedStages = job.sourceMeta?.bypassedStages ?? DEFAULT_MIGRATION_BYPASSES;
      const approvedSnapshot: Job = {
        ...job,
        intakeApproved: true,
        approvedForOrdering: true,
        planningStatus: "In Planning",
        currentStage: "Review Complete",
        sourceMeta: {
          ...(job.sourceMeta ?? {
            sourceSystem: "manual_import",
            readinessMode: "external_managed" as const,
            externalProjectId: job.id,
            importedAt: new Date().toISOString(),
            importBatchId: "manual",
            migrationStatus: "approved" as const,
            reviewFlags: [],
          }),
          migrationStatus: "approved" as const,
          bypassedStages,
        },
      };
      setPostApproveJob(approvedSnapshot);
    }
  };

  const handlePostApproveChain = (step: PushNextStep) => {
    const job = postApproveJob;
    setPostApproveJob(null);
    setSelectedJobKey(null);
    if (!job || step.kind === "done") return;
    if (step.kind === "navigate")      { navigate(step.to); return; }
    if (step.kind === "generate_po")   { navigate(`/ordering-queue?job=${job.id}`); return; }
    if (step.kind === "assign_crew")   { navigate("/scheduling"); return; }
    if (step.kind === "reschedule")    { navigate("/scheduling"); return; }
    if (step.kind === "view_permit")   { navigate("/permitting"); return; }
    if (step.kind === "resolve_issue") { navigate("/issues"); return; }
  };

  const rejectAndClose = async (job: Job) => {
    const rejected = await reject(job);
    if (rejected) {
      setSelectedJobKey(null);
    }
  };

  return (
    <div className="container max-w-6xl py-6 space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Migration Review Queue</h1>
        <p className="text-sm text-muted-foreground">
          Review and approve imported jobs before they enter the operational lifecycle.
          Imported jobs are invisible to opportunities, Smart Dispatch, scheduling and offers until approved.
        </p>
      </header>

      <Dialog open={Boolean(scopeDraftDialog)} onOpenChange={(open) => { if (!open) setScopeDraftDialog(null); }}>
        <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>AI Installer Scope Draft</DialogTitle>
          </DialogHeader>
          {activeScopeDraft && (
            <div className="space-y-4">
              <div className="grid gap-3 rounded-md border bg-muted/20 p-3 text-sm md:grid-cols-2">
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Confidence</div>
                  <div>{Math.round((activeScopeDraft.confidence ?? 0) * 100)}%</div>
                </div>
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Work area</div>
                  <div>{activeScopeDraft.workArea || "Not specified"}</div>
                </div>
              </div>

              <div className="space-y-1">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Generated Scope - Edit Before Applying
                </div>
                <Textarea
                  rows={14}
                  value={activeScopeDraft.scopeSummary ?? ""}
                  onChange={(event) => {
                    const nextScope = event.target.value;
                    setScopeDraftDialog((current) =>
                      current
                        ? {
                            ...current,
                            generation: {
                              ...current.generation,
                              scopeSummary: nextScope,
                            },
                          }
                        : current,
                    );
                  }}
                  className="font-mono text-xs"
                />
                <div className="text-xs text-muted-foreground">
                  Make any operator edits here, then apply. The AI draft is cached, but the job scope is not changed until you click Apply to Scope.
                </div>
              </div>

              {(activeScopeColorSelections.length > 0 || activeScopePanelBreakdown.length > 0) && (
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="rounded-md border p-3">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Color / Finish Selections
                    </div>
                    {activeScopeColorSelections.length > 0 ? (
                      <ul className="mt-2 list-disc space-y-1 pl-4 text-sm">
                        {activeScopeColorSelections.map((selection, index) => (
                          <li key={`${selection}-${index}`}>{selection}</li>
                        ))}
                      </ul>
                    ) : (
                      <div className="mt-2 text-sm text-muted-foreground">No colors found in the contract.</div>
                    )}
                  </div>
                  <div className="rounded-md border p-3">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Panel Breakdown
                    </div>
                    {activeScopePanelBreakdown.length > 0 ? (
                      <ul className="mt-2 list-disc space-y-1 pl-4 text-sm">
                        {activeScopePanelBreakdown.map((panel, index) => (
                          <li key={`${panel}-${index}`}>{panel}</li>
                        ))}
                      </ul>
                    ) : (
                      <div className="mt-2 text-sm text-muted-foreground">No panel breakdown found.</div>
                    )}
                  </div>
                </div>
              )}

              {(activeScopeWarnings.length > 0 || activeScopeMissingFields.length > 0) && (
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3">
                    <div className="text-xs font-medium uppercase tracking-wide text-amber-700">Warnings</div>
                    {activeScopeWarnings.length > 0 ? (
                      <ul className="mt-2 list-disc space-y-1 pl-4 text-sm">
                        {activeScopeWarnings.map((warning, index) => (
                          <li key={`${warning}-${index}`}>{warning}</li>
                        ))}
                      </ul>
                    ) : (
                      <div className="mt-2 text-sm text-muted-foreground">No warnings returned.</div>
                    )}
                  </div>
                  <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3">
                    <div className="text-xs font-medium uppercase tracking-wide text-destructive">Missing Fields</div>
                    {activeScopeMissingFields.length > 0 ? (
                      <ul className="mt-2 list-disc space-y-1 pl-4 text-sm">
                        {activeScopeMissingFields.map((field, index) => (
                          <li key={`${field}-${index}`}>{field}</li>
                        ))}
                      </ul>
                    ) : (
                      <div className="mt-2 text-sm text-muted-foreground">No missing fields returned.</div>
                    )}
                  </div>
                </div>
              )}

              {activeScopeExtractedMaterials.length > 0 && (
                <div className="space-y-2 rounded-md border p-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Material Hints From Contract - Select & Edit
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Selected hints will be merged into this staged job&apos;s material review list.
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button type="button" size="sm" variant="outline" onClick={() => setAllScopeMaterialHintsSelected(true)}>
                        Select all
                      </Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => setAllScopeMaterialHintsSelected(false)}>
                        Clear
                      </Button>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {activeScopeSelectedMaterialCount} of {activeScopeExtractedMaterials.length} selected.
                  </div>
                  <div className="max-h-[46vh] space-y-2 overflow-y-auto pr-1">
                    {activeScopeExtractedMaterials.map((material, index) => {
                      const selected = activeScopeSelectedMaterialIndexSet.has(index);
                      return (
                        <div
                          key={`${material.itemName}-${material.sku}-${index}`}
                          className="grid gap-2 rounded bg-muted/30 p-2 text-sm md:grid-cols-[28px_minmax(0,1.25fr)_180px_90px_120px]"
                        >
                          <label className="flex items-center justify-center pt-6">
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={(event) => toggleScopeMaterialHint(index, event.target.checked)}
                              aria-label={`Include material hint ${index + 1}`}
                            />
                          </label>
                          <label className="space-y-1">
                            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                              Material
                            </span>
                            <Input
                              value={material.itemName ?? ""}
                              onChange={(event) => updateScopeMaterialHint(index, { itemName: event.target.value })}
                              placeholder="Material name"
                            />
                          </label>
                          <div className="space-y-1">
                            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                              SKU
                            </span>
                            <Input
                              className="font-mono text-xs"
                              value={material.sku ?? ""}
                              onChange={(event) => updateScopeMaterialHint(index, { sku: normalizeMaterialSku(event.target.value) })}
                              placeholder="SKU or leave blank"
                            />
                            {renderProductSkuMatcher({
                              matcherId: `ai-material-hint:${scopeDraftDialog?.rowId ?? "draft"}:${index}`,
                              sku: material.sku,
                              onMatch: (product) => updateScopeMaterialHint(index, {
                                sku: normalizeMaterialSku(product.sku),
                                itemName: product.productName,
                              }),
                            })}
                          </div>
                          <label className="space-y-1">
                            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                              Qty
                            </span>
                            <Input
                              type="number"
                              min={1}
                              value={material.quantity || 1}
                              onChange={(event) => updateScopeMaterialHint(index, { quantity: Number(event.target.value) || 1 })}
                            />
                          </label>
                          <div className="space-y-1">
                            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                              Confidence
                            </div>
                            <div className="rounded-md border bg-background px-3 py-2 text-sm">
                              {Math.round((material.confidence ?? 0) * 100)}%
                            </div>
                          </div>
                          <label className="space-y-1 md:col-start-2 md:col-end-6">
                            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                              Notes
                            </span>
                            <Input
                              value={material.notes ?? ""}
                              onChange={(event) => updateScopeMaterialHint(index, { notes: event.target.value })}
                              placeholder="Optional material note from contract"
                            />
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {activeScopeExtractedMaterials.length === 0 && (
                <div className="rounded-md border p-3 text-sm text-muted-foreground">
                  No material hints were extracted from this contract.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setScopeDraftDialog(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!activeScopeDraft || busyImportRowId === scopeDraftDialog?.rowId}
              onClick={() => void applyGeneratedScopeDraft()}
            >
              {activeScopeSelectedMaterialCount > 0
                ? `Apply Scope + ${activeScopeSelectedMaterialCount} Material${activeScopeSelectedMaterialCount === 1 ? "" : "s"}`
                : "Apply to Scope"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(selectedJobKey)} onOpenChange={(open) => { if (!open) { setSelectedJobKey(null); setPostApproveJob(null); } }}>
        <Tabs defaultValue="queue">
        <TabsList>
          <TabsTrigger value="queue">
            Queue
            <Badge variant="secondary" className="ml-2">{pending.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="contract">Contract Upload</TabsTrigger>
          <TabsTrigger value="csv">CSV Import</TabsTrigger>
          <TabsTrigger value="json">JSON Import (Advanced)</TabsTrigger>
        </TabsList>

        <TabsContent value="queue" className="space-y-3">
          {pending.length === 0 ? (
            <Card className="p-8 text-center text-sm text-muted-foreground">
              No imports awaiting review.
            </Card>
          ) : pending.map((job, index) => {
            const flags = computeReviewFlags(job);
            const stagedRow = findStagedRowForJob(job);
            const addressLine = [job.address?.street, job.address?.city].filter(Boolean).join(", ") || "—";
            return (
              <Card key={stagedRow?.id ?? `${job.id}-${index}`} className="p-4 space-y-3">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0 space-y-1">
                    <div className="truncate font-medium">
                      {job.customerName || <span className="text-muted-foreground">(no customer name)</span>}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {addressLine}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {job.projectType || "Other"}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setSelectedJobKey(getJobQueueKey(job))}>
                    Open details
                  </Button>
                </div>
                {flags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {flags.map((f) => (
                      <Badge
                        key={f}
                        variant={BLOCKING_FLAGS.has(f) ? "destructive" : "secondary"}
                      >
                        {f.replace(/_/g, " ")}
                      </Badge>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </TabsContent>

        <TabsContent value="contract" className="space-y-4">
          <Card className="space-y-4 p-4">
            <div className="space-y-1">
              <h2 className="text-lg font-semibold">Create queue entry from contract</h2>
              <p className="text-sm text-muted-foreground">
                Upload a PDF contract, generate a preview, confirm the extracted fields, then stage it as a migration review item.
              </p>
            </div>

            <div className="rounded-md border bg-muted/20 p-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Contract file</div>
                  <div className="truncate text-sm">
                    {manualContractFile
                      ? `${manualContractFile.name}${formatFileSize(manualContractFile.size) ? ` · ${formatFileSize(manualContractFile.size)}` : ""}`
                      : "No file selected"}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    PDF preview extracts customer, address, amount, quote/job number, colors, and installer scope. DOC/DOCX can still be staged manually later.
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button asChild size="sm" variant="outline" className="cursor-pointer">
                    <label>
                      Choose contract
                      <input
                        className="hidden"
                        type="file"
                        accept="application/pdf,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        disabled={isManualContractBusy}
                        onChange={(event) => {
                          const file = event.target.files?.[0] ?? null;
                          if (file && !isAllowedContractFile(file)) {
                            toast.warning("Upload a PDF, DOC, or DOCX contract file.");
                            event.target.value = "";
                            return;
                          }
                          setManualContractFile(file);
                          setManualContractPreview(null);
                          setManualContractAiScopeDraft(null);
                          event.target.value = "";
                        }}
                      />
                    </label>
                  </Button>
                  {manualContractFile && isPdfContractDocument({ fileName: manualContractFile.name, mimeType: manualContractFile.type, filePath: manualContractFile.name } as Job["contractDocument"]) && (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={isManualContractPreviewBusy || isManualContractBusy}
                      onClick={() => void previewManualContract()}
                    >
                      {isManualContractPreviewBusy ? "Reading..." : manualContractPreview ? "Refresh preview" : "Generate preview"}
                    </Button>
                  )}
                  {manualContractFile && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      disabled={isManualContractBusy}
                      onClick={() => {
                        setManualContractFile(null);
                        setManualContractPreview(null);
                        setManualContractAiScopeDraft(null);
                      }}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {!manualContractFile ? (
              <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                Choose a contract to begin. Nothing is added to the queue until you review the preview and click Create queue entry.
              </div>
            ) : manualContractFile && !manualContractPreview && isPdfContractDocument({ fileName: manualContractFile.name, mimeType: manualContractFile.type, filePath: manualContractFile.name } as Job["contractDocument"]) ? (
              <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                Generate the preview first so we can extract the customer name, address, amount, and quote/job number before adding it to the queue.
              </div>
            ) : (
              <div className="space-y-4 rounded-md border bg-background p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="font-medium">Queue Preview</div>
                    <div className="text-xs text-muted-foreground">
                      Review and edit these fields. These exact values will be saved to the staged import row.
                    </div>
                  </div>
                  {manualContractPreview && (
                    <Badge variant="secondary">
                      AI confidence {Math.round((manualContractPreview.confidence ?? 0) * 100)}%
                    </Badge>
                  )}
                </div>

                {(manualContractPreview?.warnings.length || manualContractPreview?.missingFields.length) ? (
                  <div className="grid gap-3 md:grid-cols-2">
                    {manualContractPreview.warnings.length > 0 && (
                      <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3">
                        <div className="text-xs font-medium uppercase tracking-wide text-amber-700">Warnings</div>
                        <ul className="mt-2 list-disc space-y-1 pl-4 text-sm">
                          {manualContractPreview.warnings.map((warning, index) => (
                            <li key={`${warning}-${index}`}>{warning}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {manualContractPreview.missingFields.length > 0 && (
                      <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3">
                        <div className="text-xs font-medium uppercase tracking-wide text-destructive">Missing Fields</div>
                        <ul className="mt-2 list-disc space-y-1 pl-4 text-sm">
                          {manualContractPreview.missingFields.map((field, index) => (
                            <li key={`${field}-${index}`}>{field}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : null}

                <div className="grid gap-3 md:grid-cols-3">
                  <div className="space-y-1">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Customer name</div>
                    <Input
                      value={manualContractCustomerName}
                      onChange={(event) => setManualContractCustomerName(event.target.value)}
                      placeholder="Customer name"
                      disabled={isManualContractBusy}
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Quote #</div>
                    <Input
                      value={manualContractQuoteNumber}
                      onChange={(event) => setManualContractQuoteNumber(event.target.value)}
                      placeholder="Quote number from contract"
                      disabled={isManualContractBusy}
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Job / project #</div>
                    <Input
                      value={manualContractProjectId}
                      onChange={(event) => setManualContractProjectId(event.target.value)}
                      placeholder="Used as staged job number"
                      disabled={isManualContractBusy}
                    />
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-3">
                  <div className="space-y-1">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Contract amount</div>
                    <Input
                      type="number"
                      min={0}
                      step={0.01}
                      value={manualContractAmount}
                      onChange={(event) => setManualContractAmount(event.target.value)}
                      placeholder="0.00"
                      disabled={isManualContractBusy}
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Signed date</div>
                    <Input
                      value={manualContractSignedDate}
                      onChange={(event) => setManualContractSignedDate(event.target.value)}
                      placeholder="YYYY-MM-DD or contract date"
                      disabled={isManualContractBusy}
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Project type</div>
                    <select
                      value={manualContractProjectType}
                      onChange={(event) => setManualContractProjectType(event.target.value as ProjectType)}
                      disabled={isManualContractBusy}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {PROJECT_TYPES.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-1">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Email</div>
                    <Input
                      value={manualContractEmail}
                      onChange={(event) => setManualContractEmail(event.target.value)}
                      placeholder="Customer email"
                      disabled={isManualContractBusy}
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Phone</div>
                    <Input
                      type="tel"
                      inputMode="numeric"
                      value={manualContractPhone}
                      onChange={(event) => setManualContractPhone(formatPhoneNumber(event.target.value))}
                      placeholder="Customer phone"
                      disabled={isManualContractBusy}
                    />
                  </div>
                </div>

                <div className="grid gap-3 md:grid-cols-[1.5fr_1fr_120px_140px]">
                  <div className="space-y-1">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Address</div>
                    <Input
                      value={manualContractAddressLine1}
                      onChange={(event) => setManualContractAddressLine1(event.target.value)}
                      placeholder="Street address"
                      disabled={isManualContractBusy}
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">City</div>
                    <Input
                      value={manualContractCity}
                      onChange={(event) => setManualContractCity(event.target.value)}
                      placeholder="City"
                      disabled={isManualContractBusy}
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">State</div>
                    <Input
                      value={manualContractState}
                      onChange={(event) => setManualContractState(event.target.value)}
                      placeholder="State"
                      disabled={isManualContractBusy}
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">ZIP</div>
                    <Input
                      value={manualContractPostalCode}
                      onChange={(event) => setManualContractPostalCode(event.target.value)}
                      placeholder="Postal code"
                      disabled={isManualContractBusy}
                    />
                  </div>
                </div>

                {manualContractAiScopeDraft?.scopeSummary && (
                  <div className="rounded-md border bg-muted/20 p-3">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Cached installer scope preview</div>
                    <div className="mt-2 max-h-40 overflow-y-auto whitespace-pre-wrap text-xs text-muted-foreground">
                      {manualContractAiScopeDraft.scopeSummary}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-between gap-3">
              <div className="text-xs text-muted-foreground">
                {manualContractPreview
                  ? "Preview confirmed fields will be saved to the queue row."
                  : "PDF contracts require preview before creating a queue entry."}
              </div>
              <Button
                type="button"
                disabled={
                  !manualContractFile ||
                  isManualContractBusy ||
                  isManualContractPreviewBusy ||
                  (isPdfContractDocument({ fileName: manualContractFile.name, mimeType: manualContractFile.type, filePath: manualContractFile.name } as Job["contractDocument"]) && !manualContractPreview)
                }
                onClick={() => void createQueueEntryFromContract()}
              >
                {isManualContractBusy ? "Creating..." : "Create queue entry"}
              </Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="csv" className="space-y-4">
          <div className="text-sm text-muted-foreground">
            Upload a CSV file to import jobs. Expected headers: <code>project_id, customer_name, phone, email, street, city, state, zip, install_type, scope, start_at, end_at, total, contractor, signature_date, notes, quote_number</code>.
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={downloadMigrationTemplate}>
              <Download className="mr-2 h-4 w-4" /> Download Template
            </Button>
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm font-medium hover:bg-muted">
              <Upload className="h-4 w-4" />
              {isCsvReading ? "Reading…" : csvFileName ? csvFileName : "Choose File"}
              <Input
                type="file"
                accept=".csv,text/csv"
                className="sr-only"
                disabled={isCsvReading}
                onChange={async (e) => {
                  const file = e.target.files?.[0] ?? null;
                  e.currentTarget.value = "";
                  if (!file) return;
                  if (!file.name.toLowerCase().endsWith(".csv") && file.type !== "text/csv" && file.type !== "application/vnd.ms-excel" && file.type !== "") {
                    toast.error("Please upload a CSV file.");
                    return;
                  }
                  setIsCsvReading(true);
                  try {
                    const text = await file.text();
                    setCsvText(text);
                    setCsvFileName(file.name);
                  } catch (err) {
                    toast.error(err instanceof Error ? err.message : "Could not read CSV file.");
                  } finally {
                    setIsCsvReading(false);
                  }
                }}
              />
            </label>
            {csvText.trim() && (
              <button
                type="button"
                className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                onClick={() => { setCsvText(""); setCsvFileName(""); }}
              >
                Clear
              </button>
            )}
          </div>

          {/* ── Preview table (auto-updates from textarea content) ── */}
          {csvPreviewRows !== null && (() => {
            const readyCount = csvPreviewRows.filter((r) => !r.isDuplicate).length;
            const dupCount = csvPreviewRows.filter((r) => r.isDuplicate).length;
            const warnCount = csvPreviewRows.filter((r) => !r.isDuplicate && r.warnings.length > 0).length;
            return (
              <div className="space-y-3">
                {/* Summary bar */}
                <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                  <FileText className="h-3.5 w-3.5 shrink-0" />
                  {csvFileName && <span className="font-medium text-foreground">{csvFileName}</span>}
                  <span>
                    {csvPreviewRows.length} row{csvPreviewRows.length !== 1 ? "s" : ""}
                    {dupCount > 0 ? ` · ${dupCount} duplicate${dupCount !== 1 ? "s" : ""}` : ""}
                    {warnCount > 0 ? ` · ${warnCount} with warnings` : ""}
                  </span>
                </div>

                {/* Table */}
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/40 text-xs uppercase tracking-[0.14em] text-muted-foreground">
                        <th className="px-3 py-2 text-left font-medium">#</th>
                        <th className="px-3 py-2 text-left font-medium">Job ID</th>
                        <th className="px-3 py-2 text-left font-medium">Customer</th>
                        <th className="px-3 py-2 text-left font-medium">Address</th>
                        <th className="px-3 py-2 text-left font-medium">Install Type</th>
                        <th className="px-3 py-2 text-left font-medium">Start</th>
                        <th className="px-3 py-2 text-left font-medium">End</th>
                        <th className="px-3 py-2 text-left font-medium">Contractor</th>
                        <th className="px-3 py-2 text-left font-medium">Quote #</th>
                        <th className="px-3 py-2 text-right font-medium tabular-nums">Total</th>
                        <th className="px-3 py-2 text-left font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvPreviewRows.map((row, i) => (
                        <tr key={`${row.projectId}-${i}`} className="border-b last:border-0 hover:bg-muted/20">
                          <td className="px-3 py-2 text-xs tabular-nums text-muted-foreground">{i + 1}</td>
                          <td className="px-3 py-2 font-mono text-xs">{row.projectId || <span className="text-muted-foreground">—</span>}</td>
                          <td className="px-3 py-2">{row.customerName || <span className="text-muted-foreground">—</span>}</td>
                          <td className="px-3 py-2 text-xs text-muted-foreground">{row.address || "—"}</td>
                          <td className="px-3 py-2 text-xs">{row.installType || <span className="text-muted-foreground">Other</span>}</td>
                          <td className="px-3 py-2 text-xs tabular-nums">{row.startAt || "—"}</td>
                          <td className="px-3 py-2 text-xs tabular-nums">{row.endAt || <span className="text-muted-foreground">—</span>}</td>
                          <td className="px-3 py-2 text-xs">{row.contractor || <span className="text-muted-foreground">—</span>}</td>
                          <td className="px-3 py-2 font-mono text-xs">{row.quoteNumber || <span className="text-muted-foreground">—</span>}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {row.total > 0 ? `$${row.total.toLocaleString()}` : <span className="text-muted-foreground">—</span>}
                          </td>
                          <td className="px-3 py-2">
                            {row.isDuplicate ? (
                              <Badge variant="outline" className="border-orange-400/60 text-orange-500 text-xs">Duplicate</Badge>
                            ) : row.warnings.length > 0 ? (
                              <span className="text-xs text-yellow-600" title={row.warnings.join("; ")}>
                                ⚠ {row.warnings[0]}{row.warnings.length > 1 ? ` +${row.warnings.length - 1}` : ""}
                              </span>
                            ) : (
                              <Badge variant="secondary" className="text-xs text-emerald-600">Ready</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Stage action */}
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs text-muted-foreground">
                    {readyCount} job{readyCount !== 1 ? "s" : ""} will be staged for review.
                    {dupCount > 0 ? ` ${dupCount} duplicate${dupCount !== 1 ? "s" : ""} will be skipped.` : ""}
                  </p>
                  <Button
                    disabled={readyCount === 0}
                    onClick={() => void handleImport(csvText, "csv")}
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    Stage {readyCount} job{readyCount !== 1 ? "s" : ""} to queue
                  </Button>
                </div>
              </div>
            );
          })()}
        </TabsContent>

        <TabsContent value="json" className="space-y-3">
          <div className="text-sm text-muted-foreground">
            Paste Jobber-shaped JSON — single project or an array.
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={downloadExcelTemplate}>
              <Download className="mr-2 h-4 w-4" /> Download Excel Template
            </Button>
            <Input
              type="file"
              accept=".xlsx,.json"
              className="max-w-xs"
              disabled={isExcelBusy}
              onChange={handleExcelUpload}
            />
            <span className="text-xs text-muted-foreground">
              Download the template, fill it in Excel, then upload. The JSON will appear above for review before importing.
            </span>
          </div>
          <Textarea
            rows={10}
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            placeholder='[{"id":"JBR-001","title":"Master shower","client":{"name":"Jane Doe"},"property":{"street":"123 Main","city":"Tampa","state":"FL","zip":"33602"},"custom_fields":{"install_type":"Shower Remodel"},"start_at":"2026-06-15","total":8500}]'
            className="font-mono text-xs"
          />
          <Button onClick={() => handleImport(jsonText, "json")} disabled={!jsonText.trim()}>Import JSON</Button>
        </TabsContent>
        {selectedJob && (
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedJob.customerName || "Migration review"}</DialogTitle>
            </DialogHeader>

            {postApproveJob ? (
              <div className="py-2">
                <PushNextStepPanel
                  job={postApproveJob}
                  openIssues={[] as Issue[]}
                  headline="Migration approved"
                  summary={`${postApproveJob.customerName || postApproveJob.id} is now visible to operations. Pick the next step.`}
                  autoArm
                  onContinue={handlePostApproveChain}
                  onPause={() => { setPostApproveJob(null); setSelectedJobKey(null); }}
                />
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  <div className="rounded-md border p-3 text-sm">
                    <div className="font-medium">{selectedJob.customerName || "Unknown customer"}</div>
                    <div className="text-xs text-muted-foreground">
                      {[selectedJob.address?.street, selectedJob.address?.city].filter(Boolean).join(", ") || "—"}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {selectedJob.projectType || "Other"}
                    </div>
                  </div>

                  {selectedStagedRow ? (
                    renderStagedEditor(selectedStagedRow)
                  ) : (
                    <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                      No staged import editor is available for this job.
                    </div>
                  )}
                </div>

                <DialogFooter className="sticky bottom-0 bg-background py-3">
                  <Button variant="outline" onClick={() => setSelectedJobKey(null)}>Close</Button>
                  <Button onClick={() => void approveAndClose(selectedJob)} disabled={selectedBlocked}>
                    Approve
                  </Button>
                  <Button variant="ghost" onClick={() => void rejectAndClose(selectedJob)}>
                    Reject
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        )}
      </Tabs>
      </Dialog>

      {/* Photo lightbox */}
      <Dialog open={Boolean(photoGallery)} onOpenChange={(open) => { if (!open) setPhotoGallery(null); }}>
        <DialogContent className="max-w-5xl p-2">
          <DialogHeader className="px-2 pt-2">
            <DialogTitle className="flex items-center justify-between gap-3 pr-8 text-sm">
              <span>{photoGallery?.title ?? "Photo Preview"}</span>
              {photoGallery && (
                <span className="text-xs font-normal text-muted-foreground">
                  {photoGallery.index + 1} / {photoGallery.photos.length}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {photoGallery && (
            <div className="space-y-3">
              <div className="relative flex min-h-[55vh] items-center justify-center rounded-md bg-muted/30">
                {photoGallery.photos.length > 1 && (
                  <Button
                    type="button"
                    size="icon"
                    variant="secondary"
                    className="absolute left-3 top-1/2 z-10 -translate-y-1/2"
                    onClick={() =>
                      setPhotoGallery((current) =>
                        current
                          ? {
                              ...current,
                              index: (current.index - 1 + current.photos.length) % current.photos.length,
                            }
                          : current,
                      )
                    }
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                )}

                {photoGalleryUrl ? (
                  <>
                    <img
                      src={photoGalleryUrl}
                      alt={`${photoGallery.title} photo ${photoGallery.index + 1}`}
                      className={`max-h-[70vh] w-full rounded object-contain transition-all duration-300 ease-out ${
                        photoGalleryLoading ? "scale-[0.99] opacity-60" : "scale-100 opacity-100"
                      }`}
                      onLoad={() => setPhotoGalleryLoading(false)}
                      onError={() => {
                        setPhotoGalleryError("Could not load this photo. Try refreshing.");
                        setPhotoGalleryLoading(false);
                      }}
                    />
                    {photoGalleryLoading && (
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full border bg-background/90 px-3 py-1 text-xs text-muted-foreground shadow-sm">
                        Loading next photo...
                      </div>
                    )}
                  </>
                ) : (
                  <div className="px-6 py-16 text-center text-sm text-muted-foreground">
                    {photoGalleryError || "Loading photo..."}
                  </div>
                )}

                {photoGallery.photos.length > 1 && (
                  <Button
                    type="button"
                    size="icon"
                    variant="secondary"
                    className="absolute right-3 top-1/2 z-10 -translate-y-1/2"
                    onClick={() =>
                      setPhotoGallery((current) =>
                        current
                          ? {
                              ...current,
                              index: (current.index + 1) % current.photos.length,
                            }
                          : current,
                      )
                    }
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {photoGallery.photos.length > 1 && (
                <div className="space-y-2 rounded-md border bg-muted/20 p-2">
                  <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span>
                      Showing {galleryThumbnailStart + 1}-
                      {Math.min(galleryThumbnailStart + GALLERY_THUMBNAIL_LIMIT, photoGallery.photos.length)}
                      {" "}of {photoGallery.photos.length}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs"
                        disabled={galleryThumbnailStart === 0}
                        onClick={() =>
                          setGalleryThumbnailStartByPhoto((prev) => ({
                            ...prev,
                            [galleryThumbnailKey]: Math.max(0, galleryThumbnailStart - GALLERY_THUMBNAIL_LIMIT),
                          }))
                        }
                      >
                        Prev thumbs
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs"
                        disabled={galleryThumbnailStart + GALLERY_THUMBNAIL_LIMIT >= photoGallery.photos.length}
                        onClick={() =>
                          setGalleryThumbnailStartByPhoto((prev) => ({
                            ...prev,
                            [galleryThumbnailKey]: Math.min(
                              maxGalleryThumbnailStart,
                              galleryThumbnailStart + GALLERY_THUMBNAIL_LIMIT,
                            ),
                          }))
                        }
                      >
                        Next thumbs
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                  {galleryThumbnailItems.map(({ photo, index }) => (
                    <button
                      key={`${photo}-${index}`}
                      type="button"
                      className={`overflow-hidden rounded-md border p-1 transition ${
                        index === photoGallery.index
                          ? "border-primary bg-primary/10"
                          : "border-border bg-background hover:border-primary/60"
                      }`}
                      onClick={() =>
                        setPhotoGallery((current) =>
                          current ? { ...current, index } : current,
                        )
                      }
                      title={`Open photo ${index + 1}`}
                    >
                      <ImportPhotoPreview
                        photo={photo}
                        alt={`${photoGallery.title} thumbnail ${index + 1}`}
                        className="h-14 w-full rounded object-cover"
                        placeholderClassName="h-14 w-full"
                      />
                      <div className="mt-1 text-center text-[10px] text-muted-foreground">
                        {index + 1}
                      </div>
                    </button>
                  ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <QuickRegisterProductModal
        open={quickRegisterOpen}
        onOpenChange={setQuickRegisterOpen}
        prefill={quickRegisterPrefill}
        onRegistered={(product) => {
          setCatalogProducts((prev) => [...prev, product]);
        }}
      />
    </div>
  );
}
